import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import logger, { getCorrelationId } from '@/lib/logging';
// Use local type definition to avoid SSR issues with MediaPipe
type PoseLandmarker = any;
import type {
  Settings,
  CaptureSequence,
  CapturedImage,
  AnalysisData,
  Model3D,
  OverlayState,
  AnalysisQueueItem,
  UnassignedImage,
  CaptureSettings,
  AnalysisResult,
  Landmark,
} from './types';
import { VIEW_SEQUENCE, getEffectiveViewSequence, IDX } from './constants';
import { buildCapturedViewsForSave, pickManualFloorCorrection } from './lib/build-captured-views-for-save';
import { isRemoteHandoffSearch, useRemotePostureHandoff } from './lib/use-remote-posture-handoff';
import { latestAnalysisToAutoLoad } from './lib/auto-load-latest-analysis';
import type { CaptureSource } from '@/lib/types';
import { initPoseLandmarkerForImage, initPoseLandmarkerForVideo, detectPoseInImage, getModelUrl } from '@/lib/mediaPipeService';
import {
  applyFrontalWristTowardMidline,
  applyFrontalHipIliacBias,
  applyFrontalShoulderTowardHeadBias,
  ANKLE_HEEL_BLEND_LERP,
} from '@/components/posture-analysis/lib/pose-landmark-bias';
import { analyzeAnterior, analyzePosterior, analyzeLateral, tryBuild3DModel, toAnalysisDataObject, detectView } from './lib/analysis';
import { rotateLandmarks, rotateCustomLandmarks } from '@/lib/posture-analysis/floor-correction';
import { evaluateFullPostureAnalysis } from '@/lib/posture-analysis';
import { useToast } from '@/hooks/use-toast';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import {
  SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID,
} from '@/components/posture-analysis/lib/posture-mode-strategy';
import {
  getInitialAnalysisData,
  mergeInitialCapturedImages,
} from '@/components/posture-analysis/lib/initial-analysis-handoff';
import { resolveExperimentalFeaturesEnabled, shouldOmitPelvicTilt } from '@/components/posture-analysis/lib/posture-mode-strategy';
import {
  useSelfAssessmentMobileCaptureShell,
} from '@/components/posture-analysis/self-assessment/mobile-capture-layout';
import {
  useOptionalAuth,
  useOptionalClient,
  useOptionalSettings,
} from '@/components/posture-analysis/lib/optional-hooks';
import { loadCaptureSettings, saveCaptureSettings } from './lib/capture-settings';
import { compressImage } from './lib/image-compression';

import CaptureModule, { type CaptureHandlers } from '@/components/posture-analysis/CaptureModule';
import AnalysisPanels from '@/components/posture-analysis/AnalysisPanels';
import AnalysisTiles, { type PractitionerObservations } from '@/components/posture-analysis/AnalysisTiles';
import Loader from '@/components/posture-analysis/Loader';
import { CameraIcon, UploadIcon } from '@/components/posture-analysis/Icons';
import { PhotographyTipsDrawer } from '@/components/posture-analysis/PhotographyTipsDrawer';
import {
  defaultPostureSettings,
  DEFAULT_ADULT_HEIGHT_CM,
  loadPostureSettings,
  loadPostureSettingsFromLocalStorage,
  savePostureSettings,
  savePostureSettingsToLocalStorage,
} from '@/components/posture-analysis/services/posture-settings-firebase';
import {
  trackSelfAssessmentStep,
  trackPhotoUploadSuccess,
  trackPhotoUploadError,
  trackPhotoRemoved,
  trackSelfAssessmentCompleted,
  type SelfAssessmentStep,
} from '@/lib/analytics/analytics-service';
import { detectLocaleUnit } from '@/lib/locale-unit';
import { useIsMobile, useIsNarrowViewport } from '@/hooks/use-mobile';
import {
  postureAnalysisTilesLayout,
  shouldUsePostureMobileSplit,
  computeMobileSplitCanvasCapPx,
  shouldApplyMobileSplitCanvasCap,
  postureMobileSplitAnalysisColumnClass,
} from '@/components/posture-analysis/lib/posture-mobile-split';
import { usePageHeaderVisibility } from '@/contexts/page-header-visibility-context';
import { MOBILE_BOTTOM_NAV_HEIGHT_PX, MOBILE_BOTTOM_NAV_RESERVE_PX } from '@/lib/mobile-immersive-chrome';
import { cn, formatDateTimeLocale, safeToDate, sortRecordsBySortDateDesc } from '@/lib/utils';
import { refreshClientClassificationFromPosture } from '@/components/clients/lib/refresh-client-classification';
import {
  createPostureAnalysis,
  getClientPostureAnalyses,
  getPostureAnalysis,
  updatePostureAnalysis,
} from '@/components/posture-analysis/services/posture-analyses-api';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { exportPostureAnalysisToPdf } from '@/lib/pdf-export';
import type { PostureAnalysis } from '@/lib/types';
import { SelectClientDialog } from '@/components/select-client-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, PersonStanding, Download, RotateCcw, LayoutDashboard, History, ChevronDown, Activity, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ContextualToolbar, ToolbarButtonGroup, ToolbarSeparator } from '@/components/ui/contextual-toolbar';
import { ToolbarButton } from "@/components/shared/ToolbarButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ComparisonView from '@/components/posture-analysis/ComparisonView';
import AnalysisSelector from '@/components/posture-analysis/AnalysisSelector';
import CompareUnavailableNotice from '@/components/posture-analysis/CompareUnavailableNotice';
import { captureLacksLateralView, LATERAL_VIEW_REQUIRED_NOTICE } from '@/components/posture-analysis/lib/lateral-view-notice';
import SaveAnalysisButton from '@/components/posture-analysis/SaveAnalysisButton';
import { PostureProvider } from './lib/posture-context';
import { classifyPosture } from './lib/analysis';
import { saveDraft, loadDraft, clearDraft, formatDraftTimestamp } from './lib/draft-persistence';
import type {
  DraftState,
  PelvisLandmarks3D,
  CaptureSequence as CaptureSequenceLocal,
  CustomLandmarks as CustomLandmarksLocal,
  CapturedImage as CapturedImageLocal,
  PelvisPinsMeta,
  PelvisPinKey,
} from './types';
import { computeViewFrame, projectPelvis3DToCustomByView, seedPelvis3DFromViews, updatePelvis3DFrom2D, type Pelvis2DKey } from './lib/pelvis-3d';
import { getPelvisRedLinePinKeys } from './lib/pelvis-redline';
import { inferPelvisPinsForView, pelvisPinKeysForView } from './lib/pelvis-pin-inference';
import { normalizePelvisPinTypeByHipSide, type PelvisPinType } from './lib/pelvis-pin-side';
import { plumbAnchor } from './lib/drawing';
import { resolveManualFloorCorrectionFromPoints } from '@/lib/posture-analysis/floor-correction';
import { inferSagittalInputFromCaptured } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-strategy-context';
import {
  DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
  resolvePostureOrientationStrategyId,
} from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';

const FULLSCREEN_EDIT_HISTORY_CAP = 40;

type FullscreenEditSnapshot = {
  capturedImages: Record<CaptureSequence, CapturedImage | null>;
  pelvis3d: PelvisLandmarks3D | null;
  analysisData: AnalysisData;
};

/** Keeps immersive dismiss pill aligned with posture view-tab chrome when it is on screen. */
function PostureBottomChromeStackSync({ active }: { active: boolean }) {
  const { setPostureBottomChromeStackActive } = usePageHeaderVisibility();

  useEffect(() => {
    setPostureBottomChromeStackActive(active);
    return () => setPostureBottomChromeStackActive(false);
  }, [active, setPostureBottomChromeStackActive]);

  return null;
}

interface PostureAnalysisContentProps {
  onSelfAssessmentReportReady?: (analysis: PostureAnalysis) => void;
  onAnalysisUpdate?: (analysis: PostureAnalysis) => void;
  initialAnalysis?: PostureAnalysis | null;
}

const PostureAnalysisContent: React.FC<PostureAnalysisContentProps> = ({
  onSelfAssessmentReportReady,
  onAnalysisUpdate,
  initialAnalysis,
}) => {
  const onAnalysisUpdateRef = useRef(onAnalysisUpdate);
  useEffect(() => {
    onAnalysisUpdateRef.current = onAnalysisUpdate;
  }, [onAnalysisUpdate]);

  const mode = usePostureMode();
  const location = useLocation();
  const [poseLandmarkerImage, setPoseLandmarkerImage] = useState<PoseLandmarker | null>(null);
  const [poseLandmarkerVideo, setPoseLandmarkerVideo] = useState<PoseLandmarker | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loaderText, setLoaderText] = useState('Loading AI Vision…');
  const [loaderSubText, setLoaderSubText] = useState('First load ~5–10s while caching WASM; faster next runs.');
  const [isHeightFromClient, setIsHeightFromClient] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [captureHandlers, setCaptureHandlers] = useState<CaptureHandlers | null>(null);
  const [isToolbarFloating, setIsToolbarFloating] = useState(false);
  const [showTipsDrawer, setShowTipsDrawer] = useState(false);
  const [hasShownTipsThisSession, setHasShownTipsThisSession] = useState(false);
  /** Fullscreen (zoomed) editor: show the gauges/metrics panel; toggled from the fullscreen toolbar. */
  const [fullscreenMetricsEnabled, setFullscreenMetricsEnabled] = useState(true);
  const [priorAnalyses, setPriorAnalyses] = useState<{ id: string; label: string; createdAt?: Date | null }[]>([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('assessment');
  const [comparisonAnalysis, setComparisonAnalysis] = useState<any>(null);
  const [classification, setClassification] = useState<any>(
    () => initialAnalysis?.classification ?? null,
  );
  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);
  const [draftCheckResolved, setDraftCheckResolved] = useState(false);
  const [draftWasRestored, setDraftWasRestored] = useState(false);
  const draftSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousClientIdRef = useRef<string | null>(null);
  const { client, isClientLoading } = useOptionalClient();
  const { user } = useOptionalAuth();
  const navigate = useNavigate();
  const { settings: appSettings, setSettings: setAppSettings } = useOptionalSettings();
  const experimentalFeaturesEnabled = resolveExperimentalFeaturesEnabled(
    mode,
    appSettings.experimentalFeaturesEnabled,
  );
  const isMobile = useIsMobile();
  useEffect(() => {
    if (mode.mode === 'self-assessment' && isMobile) {
      setFullscreenMetricsEnabled(false);
    }
  }, [mode.mode, isMobile]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [settings, setSettings] = useState<Settings>({
    unit: mode.requiresAuth ? 'cm' : detectLocaleUnit(),
    clientHeightCM: DEFAULT_ADULT_HEIGHT_CM,
    postureOrientationStrategyId: DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
  });
  const [centerPanelMessage, setCenterPanelMessage] = useState<string | null>(null);
  const showAnalysisPanel =
    mode.canShowAnalysisPanel && (appSettings.postureAnalysisPanelEnabled ?? true);
  const isNarrowViewport = useIsNarrowViewport();
  const { isChromeHidden } = usePageHeaderVisibility();
  // Defer viewport height to mount so SSR + first client paint agree (window is 0 until measured).
  const [windowHeight, setWindowHeight] = useState(0);
  const mobileSplitRef = useRef<HTMLDivElement | null>(null);
  const [mobileSplitHeight, setMobileSplitHeight] = useState<number | undefined>(undefined);
  const [practitionerObservations, setPractitionerObservations] = useState<PractitionerObservations>({});
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Capture Settings (hoisted from CaptureModule)
  const [captureSettings, setCaptureSettings] = useState<CaptureSettings>(() => loadCaptureSettings());

  // Persist capture settings
  useEffect(() => {
    saveCaptureSettings(captureSettings);
  }, [captureSettings]);

  // Load settings on mount or when user changes
  useEffect(() => {
    const loadSettings = async () => {
      let loadedSettings: Settings;

      if (user?.uid) {
        // Load from Firebase for authenticated users
        loadedSettings = await loadPostureSettings(user.uid);
      } else {
        // Load from localStorage for unauthenticated users
        loadedSettings = loadPostureSettingsFromLocalStorage();
      }

      // Override height from client if available
      let height = loadedSettings.clientHeightCM;
      let fromClient = false;

      if (client?.height && client.height > 0) {
        height = client.height;
        fromClient = true;
      }

      setSettings({
        ...loadedSettings,
        clientHeightCM: height,
        // App-wide setting wins; legacy posture-doc value only used as fallback.
        postureOrientationStrategyId: resolvePostureOrientationStrategyId(
          appSettings.postureOrientationStrategyId ?? loadedSettings.postureOrientationStrategyId,
          experimentalFeaturesEnabled
        ),
      });
      setIsHeightFromClient(fromClient);
    };

    loadSettings();
  }, [client, user?.uid, appSettings.postureOrientationStrategyId]);

  // Keep local posture settings in sync if the user changes the algorithm in app-wide Settings
  // while the posture analysis page is open.
  useEffect(() => {
    const desired = resolvePostureOrientationStrategyId(
      appSettings.postureOrientationStrategyId,
      experimentalFeaturesEnabled
    );
    setSettings(prev =>
      prev.postureOrientationStrategyId === desired
        ? prev
        : { ...prev, postureOrientationStrategyId: desired }
    );
  }, [appSettings.postureOrientationStrategyId, experimentalFeaturesEnabled]);

  /** Refetch “Previous Assessments” list (e.g. after save). Ignores stale responses if client changed. */
  const refreshPriorAnalyses = useCallback(
    async (forClientId: string) => {
      if (!user?.uid || !forClientId) return;
      try {
        logger.debug('Refreshing posture analyses for client:', forClientId);
        const list = await getClientPostureAnalyses(forClientId);

        const validAnalyses = list.filter(a => {
          const analysisClientId = (a as any).clientId;
          if (analysisClientId && analysisClientId !== forClientId) {
            logger.warn('Found analysis with mismatched clientId:', {
              analysisId: a.id,
              expectedClientId: forClientId,
              actualClientId: analysisClientId
            });
            return false;
          }
          return true;
        });

        logger.debug(`Fetched ${list.length} analyses, ${validAnalyses.length} valid for client ${forClientId}`);

        const formatted = sortRecordsBySortDateDesc(validAnalyses).map(a => {
          const raw: any = (a as any).date ?? (a as any).createdAt;
          const d = safeToDate(raw);
          return {
            id: a.id,
            label: formatDateTimeLocale(d),
            createdAt: d,
          };
        });

        if (client?.id === forClientId) {
          setPriorAnalyses(formatted);
        } else {
          logger.warn('Client changed during prior-analyses refresh, discarding results');
        }
      } catch (error) {
        logger.error('Error fetching prior analyses:', error);
        if (client?.id === forClientId) {
          setPriorAnalyses([]);
        }
      }
    },
    [user?.uid, client?.id]
  );

  // Load prior analyses when client changes
  useEffect(() => {
    if (!client?.id || !user?.uid) {
      setPriorAnalyses([]);
      return;
    }

    setPriorAnalyses([]);
    void refreshPriorAnalyses(client.id);
  }, [client?.id, user?.uid, refreshPriorAnalyses]);

  // Save settings when they change (debounced)
  useEffect(() => {
    // Don't save if height is from client (it's already saved with the client)
    if (isHeightFromClient) return;

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce saves to avoid too many writes
    saveTimeoutRef.current = setTimeout(async () => {
      if (user?.uid) {
        // Save to Firebase for authenticated users
        await savePostureSettings(user.uid, settings);
      } else {
        // Save to localStorage for unauthenticated users
        savePostureSettingsToLocalStorage(settings);
      }
    }, 1000); // 1 second debounce

    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [settings, user?.uid, isHeightFromClient]);

  const [capturedImages, setCapturedImages] = useState<Record<CaptureSequence, CapturedImage | null>>(
    () => mergeInitialCapturedImages(initialAnalysis),
  );
  const capturedImagesRef = useRef<Record<CaptureSequence, CapturedImage | null>>(capturedImages);
  useEffect(() => {
    capturedImagesRef.current = capturedImages;
  }, [capturedImages]);

  const effectiveSequence = useMemo((): CaptureSequence[] => {
    if (mode.allowedViews.length < VIEW_SEQUENCE.length) {
      return [...mode.allowedViews];
    }
    return getEffectiveViewSequence(experimentalFeaturesEnabled);
  }, [mode.allowedViews, experimentalFeaturesEnabled]);

  const isAllCaptured = useMemo(() => {
    return effectiveSequence.every((v) => capturedImages[v] && capturedImages[v]?.status !== 'failed');
  }, [effectiveSequence, capturedImages]);

  // Resolve the orientation-classification strategy (experimental gates non-default strategies).
  const effectivePostureStrategyId = useMemo(() => {
    if (mode.mode === 'self-assessment') {
      return SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID;
    }
    return resolvePostureOrientationStrategyId(
      settings.postureOrientationStrategyId,
      experimentalFeaturesEnabled,
    );
  }, [mode.mode, settings.postureOrientationStrategyId, experimentalFeaturesEnabled]);

  // Options threaded into every classifyPosture(...) call: strategy id, captured views for
  // strategy context, and the inferred sagittal input from the captured lateral view.
  const classifyOptions = useMemo(() => {
    const { sagittal } = inferSagittalInputFromCaptured(capturedImages);
    const hasObs = Object.values(practitionerObservations).some(Boolean);
    const strategyInput = {
      ...(sagittal ? { sagittal } : {}),
      ...(hasObs ? { practitionerObservations } : {}),
    };
    return {
      strategyId: effectivePostureStrategyId,
      capturedForStrategy: capturedImages,
      ...(Object.keys(strategyInput).length > 0 ? { strategyInput } : {}),
    };
  }, [effectivePostureStrategyId, capturedImages, practitionerObservations]);

  const [unassignedImages, setUnassignedImages] = useState<UnassignedImage[]>([]);
  const [analysisData, setAnalysisData] = useState<AnalysisData>(
    () => getInitialAnalysisData(initialAnalysis),
  );
  const [model3D, setModel3D] = useState<Model3D | null>(null);
  const [currentView, setCurrentViewState] = useState<CaptureSequence | null>(null);

  useEffect(() => {
    if (initialAnalysis?.captured) {
      setCapturedImages((prev) => {
        const next = { ...prev };
        (Object.keys(initialAnalysis.captured) as CaptureSequence[]).forEach((v) => {
          if (initialAnalysis.captured[v]) {
            next[v] = initialAnalysis.captured[v];
          }
        });
        return next;
      });
      setCurrentViewState('Anterior');
    }
    const initialData = getInitialAnalysisData(initialAnalysis);
    if (Object.keys(initialData).length > 0) {
      setAnalysisData(initialData);
    }
    if (initialAnalysis?.classification) {
      setClassification(initialAnalysis.classification);
    }
  }, [initialAnalysis]);

  // Track step navigation and completion in self-assessment mode
  // (must run after currentView / capturedImages are declared — TDZ)
  const hasTrackedStartRef = useRef(false);
  const completedTrackedRef = useRef(false);

  useEffect(() => {
    if (mode.mode !== 'self-assessment') return;

    if (!hasTrackedStartRef.current) {
      hasTrackedStartRef.current = true;
      trackSelfAssessmentStep('start');
    }

    if (currentView) {
      const stepMap: Record<string, SelfAssessmentStep> = {
        Anterior: 'anterior',
        Left: 'lateral_left',
        Right: 'lateral_right',
        Posterior: 'posterior',
      };
      const step = stepMap[currentView];
      if (step) {
        trackSelfAssessmentStep(step);
      }
    }
  }, [mode.mode, currentView]);

  useEffect(() => {
    if (mode.mode !== 'self-assessment') return;

    const completedCount = Object.values(capturedImages).filter(
      img => img?.status === 'complete'
    ).length;

    if (completedCount >= 3 && !completedTrackedRef.current) {
      completedTrackedRef.current = true;
      trackSelfAssessmentCompleted(completedCount);
    }
  }, [mode.mode, capturedImages]);

  // Synchronize ankle landmarks and metrics when the ankle-heel blend toggle is updated
  useEffect(() => {
    const disableBlend = !!practitionerObservations.disableAnkleHeelBlend;
    
    let capturedImagesChanged = false;
    const nextCapturedImages = { ...capturedImages };
    const nextAnalysisData = { ...analysisData };
    
    for (const view of VIEW_SEQUENCE) {
      const img = nextCapturedImages[view];
      if (img && img.landmarks) {
        const raw = img.rawLandmarks;
        if (!raw) continue;
        
        let landmarksChangedForView = false;
        const nextLandmarks = img.landmarks.map((lm, idx) => {
          if (idx === IDX.L_ANK || idx === IDX.R_ANK) {
            const isLeft = idx === IDX.L_ANK;
            const heelIdx = isLeft ? IDX.L_HEEL : IDX.R_HEEL;
            const rawAnkle = raw[idx];
            const rawHeel = raw[heelIdx];
            
            if (rawAnkle && rawHeel) {
              const blendedX = rawAnkle.x + (rawHeel.x - rawAnkle.x) * ANKLE_HEEL_BLEND_LERP;
              const blendedY = rawAnkle.y + (rawHeel.y - rawAnkle.y) * ANKLE_HEEL_BLEND_LERP;
              
              const isAtRaw = Math.abs(lm.x - rawAnkle.x) < 1e-4 && Math.abs(lm.y - rawAnkle.y) < 1e-4;
              const isAtBlended = Math.abs(lm.x - blendedX) < 1e-4 && Math.abs(lm.y - blendedY) < 1e-4;
              
              if (isAtRaw || isAtBlended) {
                const targetX = disableBlend ? rawAnkle.x : blendedX;
                const targetY = disableBlend ? rawAnkle.y : blendedY;
                
                if (lm.x !== targetX || lm.y !== targetY) {
                  landmarksChangedForView = true;
                  return { ...lm, x: targetX, y: targetY };
                }
              }
            }
          }
          return lm;
        });
        
        if (landmarksChangedForView) {
          capturedImagesChanged = true;
          nextCapturedImages[view] = { ...img, landmarks: nextLandmarks };
          
          if (img.cmPerPx) {
            const deg = img.floorCorrectionDeg ?? 0;
            let rows: AnalysisResult[] = [];
            if (view === 'Anterior') {
              rows = analyzeAnterior(rotateLandmarks(nextLandmarks, deg), view, img.cmPerPx, settings.unit, rotateCustomLandmarks(img.custom, deg));
            } else if (view === 'Posterior') {
              rows = analyzePosterior(rotateLandmarks(nextLandmarks, deg), view, img.cmPerPx, settings.unit, rotateCustomLandmarks(img.custom, deg));
            } else if (view === 'Left' || view === 'Right') {
              rows = analyzeLateral(
                rotateLandmarks(nextLandmarks, deg),
                view,
                img.cmPerPx,
                settings.unit,
                rotateCustomLandmarks(img.custom, deg),
                { omitPelvicTilt: !!img.pelvisPinsOptOut }
              );
            }
            nextAnalysisData[view] = toAnalysisDataObject(rows);
          }
        }
      }
    }
    
    if (capturedImagesChanged) {
      setCapturedImages(nextCapturedImages);
      setAnalysisData(nextAnalysisData);
    }
  }, [practitionerObservations.disableAnkleHeelBlend]);

  const lastNotifiedRef = useRef<string | null>(null);

  useEffect(() => {
    const notifyParent = onAnalysisUpdateRef.current;
    if (!notifyParent) return;

    const payloadKey = JSON.stringify({
      clientHeight: settings.clientHeightCM,
      unit: settings.unit,
      capturedViews: Object.keys(capturedImages).filter(k => capturedImages[k as CaptureSequence]?.imageData),
      analysisViews: Object.keys(analysisData),
      overallPattern: classification?.pattern,
    });

    if (lastNotifiedRef.current === payloadKey) {
      return;
    }
    lastNotifiedRef.current = payloadKey;

    notifyParent({
      id: 'self-assessment',
      createdAt: new Date(),
      clientHeight: settings.clientHeightCM,
      unit: settings.unit,
      captured: capturedImages,
      analysis: analysisData,
      classification,
    } as PostureAnalysis);
  }, [capturedImages, analysisData, classification, settings.clientHeightCM, settings.unit]);

  // Wrapped setCurrentView with extensive logging
  const setCurrentView = useCallback((view: CaptureSequence | null) => {
    const previousView = currentView;
    const timestamp = new Date().toISOString();

    logger.debug('🔵 [POSTURE] setCurrentView called', {
      category: 'ui',
      previousView,
      newView: view,
      timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent,
      stackTrace: new Error().stack,
    });

    try {
      logger.debug('🟢 [POSTURE] setCurrentView: Updating state', {
        category: 'ui',
        previousView,
        newView: view,
      });

      setCurrentViewState(view);

      logger.debug('✅ [POSTURE] setCurrentView: State updated successfully', {
        category: 'ui',
        previousView,
        newView: view,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('❌ [POSTURE] setCurrentView: Error updating state', {
        category: 'ui',
        previousView,
        newView: view,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }, [currentView]);

  // Track auth state changes when view changes (practitioner mode only)
  useEffect(() => {
    if (!mode.shouldCheckSession || currentView === null) return;

    logger.debug('🔍 [POSTURE] View changed - checking auth state', {
      category: 'auth',
      view: currentView,
      timestamp: new Date().toISOString(),
    });

    // Log current auth state
    const checkAuthState = async () => {
      try {
        // Check if user is still authenticated
        const sessionResponse = await fetch('/api/auth/session', {
          credentials: 'include',
          headers: {
            'X-Correlation-Id': getCorrelationId() || 'unknown',
          },
        });

        const sessionData = await sessionResponse.json();

        logger.debug('🔍 [POSTURE] Auth session check after view change', {
          category: 'auth',
          view: currentView,
          authenticated: sessionData.authenticated,
          userId: sessionData.user?.uid || 'none',
          userEmail: sessionData.user?.email || 'none',
          isGuest: sessionData.user?.isGuest || false,
          status: sessionResponse.status,
          timestamp: new Date().toISOString(),
        });

        if (!sessionData.authenticated) {
          logger.warn('⚠️ [POSTURE] User not authenticated after view change', {
            category: 'auth',
            view: currentView,
            sessionData,
          });
        }
      } catch (error) {
        logger.error('❌ [POSTURE] Error checking auth state after view change', {
          category: 'auth',
          view: currentView,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    };

    checkAuthState();
  }, [currentView, mode.shouldCheckSession]);
  const [pelvis3d, setPelvis3d] = useState<PelvisLandmarks3D | null>(null);
  const pelvis3dRef = useRef<PelvisLandmarks3D | null>(null);
  useEffect(() => {
    pelvis3dRef.current = pelvis3d;
  }, [pelvis3d]);

  const analysisDataRef = useRef<AnalysisData>({});
  useEffect(() => {
    analysisDataRef.current = analysisData;
  }, [analysisData]);

  const fullscreenUndoStackRef = useRef<FullscreenEditSnapshot[]>([]);
  const fullscreenRedoStackRef = useRef<FullscreenEditSnapshot[]>([]);
  const [fullscreenHistoryTick, setFullscreenHistoryTick] = useState(0);

  const clearFullscreenEditHistory = useCallback(() => {
    fullscreenUndoStackRef.current = [];
    fullscreenRedoStackRef.current = [];
    setFullscreenHistoryTick((t) => t + 1);
  }, []);

  const pushFullscreenEditHistory = useCallback(() => {
    try {
      const snap: FullscreenEditSnapshot = {
        capturedImages: structuredClone(capturedImagesRef.current),
        pelvis3d: structuredClone(pelvis3dRef.current),
        analysisData: structuredClone(analysisDataRef.current),
      };
      fullscreenUndoStackRef.current = [
        ...fullscreenUndoStackRef.current.slice(-(FULLSCREEN_EDIT_HISTORY_CAP - 1)),
        snap,
      ];
      fullscreenRedoStackRef.current = [];
      setFullscreenHistoryTick((t) => t + 1);
    } catch (e) {
      logger.warn('pushFullscreenEditHistory: structuredClone failed', e);
    }
  }, []);

  const handleFullscreenUndo = useCallback(() => {
    const undoSt = fullscreenUndoStackRef.current;
    if (undoSt.length === 0) return;
    let currentSnap: FullscreenEditSnapshot;
    try {
      currentSnap = {
        capturedImages: structuredClone(capturedImagesRef.current),
        pelvis3d: structuredClone(pelvis3dRef.current),
        analysisData: structuredClone(analysisDataRef.current),
      };
    } catch (e) {
      logger.warn('handleFullscreenUndo: structuredClone failed', e);
      return;
    }
    const restore = undoSt[undoSt.length - 1]!;
    fullscreenUndoStackRef.current = undoSt.slice(0, -1);
    fullscreenRedoStackRef.current = [
      ...fullscreenRedoStackRef.current.slice(-(FULLSCREEN_EDIT_HISTORY_CAP - 1)),
      currentSnap,
    ];
    setCapturedImages(restore.capturedImages);
    setPelvis3d(restore.pelvis3d);
    pelvis3dRef.current = restore.pelvis3d;
    setAnalysisData(restore.analysisData);
    setFullscreenHistoryTick((t) => t + 1);
  }, []);

  const handleFullscreenRedo = useCallback(() => {
    const redoSt = fullscreenRedoStackRef.current;
    if (redoSt.length === 0) return;
    let currentSnap: FullscreenEditSnapshot;
    try {
      currentSnap = {
        capturedImages: structuredClone(capturedImagesRef.current),
        pelvis3d: structuredClone(pelvis3dRef.current),
        analysisData: structuredClone(analysisDataRef.current),
      };
    } catch (e) {
      logger.warn('handleFullscreenRedo: structuredClone failed', e);
      return;
    }
    const forward = redoSt[redoSt.length - 1]!;
    fullscreenRedoStackRef.current = redoSt.slice(0, -1);
    fullscreenUndoStackRef.current = [
      ...fullscreenUndoStackRef.current.slice(-(FULLSCREEN_EDIT_HISTORY_CAP - 1)),
      currentSnap,
    ];
    setCapturedImages(forward.capturedImages);
    setPelvis3d(forward.pelvis3d);
    pelvis3dRef.current = forward.pelvis3d;
    setAnalysisData(forward.analysisData);
    setFullscreenHistoryTick((t) => t + 1);
  }, []);

  const [overlayState, setOverlayState] = useState<OverlayState>({
    Anterior: { client: true, realtime: true },
    Posterior: { client: true, realtime: true },
    Left: { client: true, realtime: true },
    Right: { client: true, realtime: true },
  });

  const analysisQueueRef = useRef<AnalysisQueueItem[]>([]);
  const isProcessingRef = useRef<boolean>(false);
  const inFlightByViewRef = useRef<Set<CaptureSequence | 'Auto-detect'>>(new Set());

  const { toast } = useToast();

  const showToast = useCallback((message: string, type: 'error' | 'success' | 'info' | 'warning' = 'info', centerMessage?: string, useToast: boolean = true) => {
    const titles = { error: 'Error', success: 'Success', info: 'Info', warning: 'Warning' };
    if (useToast) {
      toast({ title: titles[type], description: message });
    }
    if (centerMessage) {
      setCenterPanelMessage(centerMessage);
    } else if (!useToast) {
      // If toast is suppressed but no center message provided, imply message is center message
      setCenterPanelMessage(message);
    }
  }, [toast]);

  // Helper function to restore draft
  const restoreDraft = useCallback((draft: DraftState) => {
    clearFullscreenEditHistory();
    setCapturedImages(draft.capturedImages);
    setAnalysisData(draft.analysisData);
    setSettings(draft.settings);
    setCurrentView(draft.currentView);
    setPelvis3d(draft.pelvis3d ?? null);
    pelvis3dRef.current = draft.pelvis3d ?? null;
    setSaveStatus('idle');
    setSelectedAnalysisId(null);
    setHasUnsavedDraft(true);
    showToast('Draft restored', 'success');
  }, [showToast, clearFullscreenEditHistory]);

  // Auto-save draft whenever assessment data changes (debounced)
  useEffect(() => {
    // Skip if assessment is already saved or no client selected
    if (saveStatus === 'saved' || !client?.id) {
      return;
    }

    // Check if there's any actual work to save (at least one image uploaded)
    const hasWork = Object.values(capturedImages).some(img => img?.status === 'complete' || img?.imageData);
    if (!hasWork) {
      // No work to save, clear any existing draft
      clearDraft(client.id).catch(err => logger.error('Failed to clear draft:', err));
      setHasUnsavedDraft(false);
      return;
    }

    // Clear any existing timeout
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }

    // Debounce draft saves to avoid excessive writes (2 seconds)
    draftSaveTimeoutRef.current = setTimeout(() => {
      const draft: DraftState = {
        capturedImages,
        analysisData,
        settings,
        currentView,
        pelvis3d,
        timestamp: Date.now(),
      };

      try {
        saveDraft(client.id, draft).then(() => {
          setHasUnsavedDraft(true);
        }).catch(error => {
          logger.error('Error auto-saving draft:', error);
          showToast(
            "Draft could not be saved due to a storage error.",
            'error',
            undefined,
            true
          );
        });
      } catch (error) {
        // catch block for synchronous errors, though mostly handled in promise now
        logger.error('Error in saveDraft wrapper:', error);
      }
    }, 2000);

    // Cleanup on unmount
    return () => {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
  }, [capturedImages, analysisData, settings, currentView, pelvis3d, saveStatus, client?.id]);

  const applyProjectedPelvisToCapturedImages = useCallback((pelvis: PelvisLandmarks3D) => {
    setCapturedImages((prev) => {
      const frames: Partial<Record<CaptureSequence, any>> = {};
      (['Anterior', 'Posterior', 'Left', 'Right'] as CaptureSequence[]).forEach((v) => {
        const img = prev[v];
        if (img?.landmarks && img.cmPerPx) {
          const frame = computeViewFrame(v, img.landmarks, img.cmPerPx);
          if (frame) frames[v] = frame;
        }
      });
      const projectedByView = projectPelvis3DToCustomByView(pelvis, frames as any);

      let changed = false;
      const next: typeof prev = { ...prev };
      (Object.keys(projectedByView) as CaptureSequence[]).forEach((v) => {
        const img = prev[v];
        if (!img) return;
        const proj = projectedByView[v];
        if (!proj) return;
        // IMPORTANT: Preserve user-placed pelvis pins.
        // Projection provides a best-effort cross-view mapping, but if the user explicitly
        // placed a pin in a given view, that pixel coordinate should remain authoritative.
        //
        // So we merge with projection first, then let existing custom values override.
        const merged: CustomLandmarksLocal = { ...(proj as any), ...(img.custom || {}) };
        // Shallow check on relevant keys (keep it cheap)
        const keys = Object.keys(proj) as (keyof CustomLandmarksLocal)[];
        const same = keys.every((k) => {
          const a = (img.custom as any)?.[k];
          const b = (merged as any)[k];
          return (a?.x === b?.x && a?.y === b?.y) || (!a && !b);
        });
        if (!same) {
          changed = true;
          next[v] = { ...img, custom: merged };
        }
      });

      return changed ? next : prev;
    });
  }, []);

  const mergeAutoPelvisMeta = useCallback(
    (
      prevMeta: PelvisPinsMeta | undefined,
      prevCustom: CustomLandmarksLocal | undefined,
      inferred: Partial<CustomLandmarksLocal>,
      confidenceByKey?: Partial<Record<PelvisPinKey, number>>
    ): PelvisPinsMeta => {
      const now = new Date().toISOString();
      const out: PelvisPinsMeta = { ...(prevMeta || {}) };
      (Object.keys(inferred) as PelvisPinKey[]).forEach((k) => {
        // Only mark auto if the user hasn't already set this point in this view.
        const alreadyHasPoint = !!(prevCustom as any)?.[k];
        if (alreadyHasPoint) return;
        if (!out[k]) {
          out[k] = { source: 'auto', confidence: confidenceByKey?.[k] ?? 0.65, updatedAt: now };
        }
      });
      return out;
    },
    []
  );

  // Auto-seed pelvis landmarks once we have any completed view(s)
  useEffect(() => {
    const captured: Partial<Record<CaptureSequence, { landmarks: any; cmPerPx: number }>> = {};
    (['Anterior', 'Posterior', 'Left', 'Right'] as CaptureSequence[]).forEach((v) => {
      const img = capturedImages[v];
      if (img?.status === 'complete' && img.landmarks && img.cmPerPx) {
        captured[v] = { landmarks: img.landmarks, cmPerPx: img.cmPerPx };
      }
    });
    if (Object.keys(captured).length === 0) return;

    // Multi-view pelvis fit:
    // 1) Start from a hip-based seed (approx scale/origin)
    // 2) Constrain by any per-view pelvis pins (auto or user)
    // 3) Project into all views to keep ASIS/PSIS coherent across captures
    const seeded = seedPelvis3DFromViews({ current: pelvis3d, captured });

    const hasAnyPins = (['Anterior', 'Posterior', 'Left', 'Right'] as CaptureSequence[]).some((v) => {
      const c = capturedImages[v]?.custom as any;
      return !!(c?.ASIS || c?.PSIS || c?.L_ASIS || c?.R_ASIS || c?.L_PSIS || c?.R_PSIS);
    });

    if (!hasAnyPins) {
      // Keep the pelvis3d seed for potential later use, but don't project pins.
      if (!pelvis3d) setPelvis3d(seeded);
      return;
    }

    let fitted = seeded;
    (['Anterior', 'Posterior', 'Left', 'Right'] as CaptureSequence[]).forEach((v) => {
      const img = capturedImages[v];
      if (!img?.landmarks || !img.cmPerPx) return;
      const frame = computeViewFrame(v, img.landmarks, img.cmPerPx);
      if (!frame) return;
      const custom = img.custom as any;
      if (!custom) return;

      const apply = (type: Pelvis2DKey, pt: { x: number; y: number } | undefined) => {
        if (!pt) return;
        fitted = updatePelvis3DFrom2D(fitted, v, type, pt, frame);
      };

      if (v === 'Anterior') {
        apply('L_ASIS', custom.L_ASIS);
        apply('R_ASIS', custom.R_ASIS);
      } else if (v === 'Posterior') {
        apply('L_PSIS', custom.L_PSIS);
        apply('R_PSIS', custom.R_PSIS);
      } else {
        apply('ASIS', custom.ASIS);
        apply('PSIS', custom.PSIS);
      }
    });

    const isSame =
      pelvis3d &&
      ['ASIS_L', 'ASIS_R', 'PSIS_L', 'PSIS_R'].every((k) => {
        const a = (pelvis3d as any)[k];
        const b = (fitted as any)[k];
        return a?.x_cm === b?.x_cm && a?.y_cm === b?.y_cm && a?.z_cm === b?.z_cm;
      });

    if (!pelvis3d || !isSame) {
      setPelvis3d(fitted);
      applyProjectedPelvisToCapturedImages(fitted);
    }
  }, [capturedImages, pelvis3d, applyProjectedPelvisToCapturedImages]);

  useEffect(() => {
    setDraftCheckResolved(false);
    setDraftWasRestored(false);
  }, [client?.id]);

  // Check for draft on mount and when client changes
  useEffect(() => {
    // Only check for drafts after initial load (skip on first render to avoid conflicts)
    const checkDraft = async () => {
      let restoredDraft = false;

      if (!client?.id) {
        // No client selected, check for guest draft
        const guestDraft = await loadDraft(null);
        if (guestDraft && !selectedAnalysisId) {
          // Check if there's already work in progress
          const hasCurrentWork = Object.values(capturedImages).some(img => img?.status === 'complete' || img?.imageData);
          if (!hasCurrentWork) {
            // Show restoration prompt for guest draft
            const timestamp = formatDraftTimestamp(new Date(guestDraft.timestamp));
            const shouldRestore = window.confirm(
              `You have an unsaved assessment from ${timestamp}. Restore it?`
            );

            if (shouldRestore) {
              restoreDraft(guestDraft);
              restoredDraft = true;
            } else {
              await clearDraft(null);
            }
          } else {
            // Current work exists, just mark draft as available
            setHasUnsavedDraft(true);
          }
        } else {
          setHasUnsavedDraft(false);
        }
        previousClientIdRef.current = null;
        setDraftWasRestored(restoredDraft);
        setDraftCheckResolved(true);
        return;
      }

      // Client changed - previous client's draft is already saved by auto-save hook
      if (previousClientIdRef.current && previousClientIdRef.current !== client.id) {
        // Client changed, just update the ref
      }

      // Check for draft for current client
      const draft = await loadDraft(client.id);
      if (draft && !selectedAnalysisId) {
        // Check if there's already work in progress
        const hasCurrentWork = Object.values(capturedImages).some(img => img?.status === 'complete' || img?.imageData);
        if (!hasCurrentWork) {
          // Show restoration prompt
          const timestamp = formatDraftTimestamp(new Date(draft.timestamp));
          const shouldRestore = window.confirm(
            `You have an unsaved assessment from ${timestamp}. Restore it?`
          );

          if (shouldRestore) {
            restoreDraft(draft);
            restoredDraft = true;
          } else {
            await clearDraft(client.id);
            setHasUnsavedDraft(false);
          }
        } else {
          // Current work exists, just mark draft as available
          setHasUnsavedDraft(true);
        }
      } else if (draft) {
        // Draft exists but we're loading a saved analysis, just mark as having draft
        setHasUnsavedDraft(true);
      } else {
        setHasUnsavedDraft(false);
      }

      previousClientIdRef.current = client.id;
      setDraftWasRestored(restoredDraft);
      setDraftCheckResolved(true);
    };

    // Delay check slightly to avoid conflicts with initial state
    const timeoutId = setTimeout(checkDraft, 100);
    return () => clearTimeout(timeoutId);
  }, [client?.id, selectedAnalysisId, restoreDraft, capturedImages]);

  const processImage = useCallback(async (imageDataUrl: string, view: CaptureSequence | 'Auto-detect') => {
    if (!poseLandmarkerImage) {
      showToast('AI Vision not ready.', 'error', undefined, false);
      return;
    }
    if (settings.clientHeightCM <= 0) {
      showToast('Enter client height first.', 'error', undefined, false);
      return;
    }

    try {
      const compressedImageDataUrl = await compressImage(imageDataUrl, 800, 0.6);

      if (view !== 'Auto-detect') {
        setCapturedImages(prev => ({ ...prev, [view]: { imageData: compressedImageDataUrl, status: 'processing', custom: prev[view]?.custom || {}, ...(shouldOmitPelvicTilt(mode, prev[view]) ? { pelvisPinsOptOut: true } : {}) } }));
      }

      const { landmarks, rawLandmarks, cmPerPx } = await detectPoseInImage(
        poseLandmarkerImage,
        compressedImageDataUrl,
        settings.clientHeightCM,
        {
          visibilityThreshold: captureSettings.visibilityThreshold,
          view: view !== 'Auto-detect' ? view : undefined,
        }
      );

      let viewName: CaptureSequence | null = null;
      if (view === 'Auto-detect') {
        // Force all uploaded images to manual assignment
        setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl: imageDataUrl }]);
        showToast(`Please assign view manually.`, 'info', undefined, false);
        return;
      } else {
        viewName = view;
      }

      if (!viewName) return;

      setCapturedImages(prev => ({ ...prev, [viewName as CaptureSequence]: { ...prev[viewName as CaptureSequence]!, imageData: compressedImageDataUrl, status: 'analyzing' } }));

      // Do NOT auto-place pelvis pins by default (estimated). Only use existing custom user pins.
      const prevCustom = (capturedImagesRef.current[viewName as CaptureSequence]?.custom || {}) as CustomLandmarksLocal;
      const prevMeta = (capturedImagesRef.current[viewName as CaptureSequence]?.pelvisPinsMeta || {}) as PelvisPinsMeta;
      const nextCustom = { ...prevCustom };
      const nextMeta = { ...prevMeta };

      // Self-assessment omits ASIS/PSIS by default; practitioner mode respects pelvisPinsOptOut.
      const isOptOut = shouldOmitPelvicTilt(
        mode,
        capturedImagesRef.current[viewName as CaptureSequence] as CapturedImageLocal | null,
      );
      const prevImage = capturedImagesRef.current[viewName as CaptureSequence] as CapturedImageLocal | null;
      let floorCorrectionDeg = prevImage?.floorCorrectionDeg ?? 0;
      let floorCorrectionSource = prevImage?.floorCorrectionSource ?? 'none';
      let floorCorrectionConfidence = prevImage?.floorCorrectionConfidence ?? 0;
      let floorCorrectionPivot = prevImage?.floorCorrectionPivot ?? null;
      let floorCorrectionWarning = prevImage?.floorCorrectionWarning;


      let analysisResult: AnalysisResult[] = [];
      if (viewName === 'Anterior') analysisResult = analyzeAnterior(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined));
      else if (viewName === 'Posterior') analysisResult = analyzePosterior(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined));
      else if (viewName === 'Left' || viewName === 'Right') analysisResult = analyzeLateral(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined), { omitPelvicTilt: isOptOut });

      const analysisObject = toAnalysisDataObject(analysisResult);

      setAnalysisData(prev => ({ ...prev, [viewName as CaptureSequence]: analysisObject }));
      setCapturedImages(prev => ({
        ...prev,
        [viewName as CaptureSequence]: {
          ...prev[viewName as CaptureSequence]!,
          landmarks,
          rawLandmarks,
          status: 'complete',
          cmPerPx,
          custom: nextCustom,
          pelvisPinsMeta: nextMeta,
          floorCorrectionDeg,
          floorCorrectionSource,
          floorCorrectionConfidence,
          floorCorrectionPivot,
          floorCorrectionWarning,
          ...(isOptOut ? { pelvisPinsOptOut: true } : {})
        }
      }));
      setCurrentView(viewName as CaptureSequence);

    } catch (err) {
      logger.error(`Error processing ${view}:`, err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Error: ${message}`, 'error', undefined, false);
      if (view !== 'Auto-detect') {
        setCapturedImages(prev => ({ ...prev, [view]: { ...prev[view], status: 'failed' } }));
      }
    }
  }, [poseLandmarkerImage, settings.clientHeightCM, settings.unit, showToast, capturedImages, captureSettings.visibilityThreshold, mode]);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || analysisQueueRef.current.length === 0) return;

    isProcessingRef.current = true;
    const item = analysisQueueRef.current.shift();

    if (item && poseLandmarkerImage) {
      const { imageDataUrl, view, resetToInitialDetection } = item;

      try {
        const compressedImageDataUrl = await compressImage(imageDataUrl, 800, 0.6);

        if (view !== 'Auto-detect') {
          setCapturedImages(prev => ({
            ...prev,
            [view]: {
              imageData: compressedImageDataUrl,
              status: 'processing',
              ...(prev[view]?.captureSource ? { captureSource: prev[view]!.captureSource } : {}),
              custom: resetToInitialDetection ? {} : (prev[view]?.custom || {}),
              pelvisPinsMeta: resetToInitialDetection ? {} : prev[view]?.pelvisPinsMeta,
              ...(shouldOmitPelvicTilt(mode, prev[view]) ? { pelvisPinsOptOut: true } : {}),
            },
          }));
        }

        const { landmarks, rawLandmarks, cmPerPx, imageWidth } = await detectPoseInImage(
          poseLandmarkerImage,
          compressedImageDataUrl,
          settings.clientHeightCM,
          {
            visibilityThreshold: captureSettings.visibilityThreshold,
            view: view !== 'Auto-detect' ? view : undefined,
          }
        );

        let viewName: CaptureSequence | null = null;
        if (view === 'Auto-detect') {
          const detected = detectView(landmarks);
          if (detected && !capturedImages[detected]) {
            viewName = detected;
            showToast(`Detected ${detected} view.`, 'success', undefined, false);
          } else {
            setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl: imageDataUrl }]);
            if (detected && capturedImages[detected]) {
              showToast(`${detected} view already captured. Please assign manually if needed.`, 'info', undefined, false);
            } else {
              showToast(`Could not auto-assign view. Please assign manually.`, 'info', undefined, false);
            }
          }
        } else {
          viewName = view;
        }

        if (viewName) {
          if (viewName === 'Anterior' && view === 'Auto-detect') {
            applyFrontalWristTowardMidline(landmarks, imageWidth);
          }
          if ((viewName === 'Anterior' || viewName === 'Posterior') && view === 'Auto-detect') {
            applyFrontalHipIliacBias(landmarks, viewName);
            applyFrontalShoulderTowardHeadBias(landmarks);
          }

          setCapturedImages(prev => ({ ...prev, [viewName]: { ...prev[viewName]!, imageData: compressedImageDataUrl, status: 'analyzing' } }));

          // Do NOT auto-place pelvis pins by default (estimated). Only use existing custom user pins.
          const prevCustom = (
            resetToInitialDetection ? {} : (capturedImagesRef.current[viewName]?.custom || {})
          ) as CustomLandmarksLocal;
          const prevMeta = (
            resetToInitialDetection ? {} : (capturedImagesRef.current[viewName]?.pelvisPinsMeta || {})
          ) as PelvisPinsMeta;
          const nextCustom = { ...prevCustom };
          const nextMeta = { ...prevMeta };

          // Self-assessment omits ASIS/PSIS by default; practitioner mode respects pelvisPinsOptOut.
          const isOptOut = shouldOmitPelvicTilt(
            mode,
            capturedImagesRef.current[viewName] as CapturedImageLocal | null,
          );
          const prevImg = capturedImagesRef.current[viewName] as CapturedImageLocal | null;
          let floorCorrectionDeg = prevImg?.floorCorrectionDeg ?? 0;
          let floorCorrectionSource = prevImg?.floorCorrectionSource ?? 'none';
          let floorCorrectionConfidence = prevImg?.floorCorrectionConfidence ?? 0;
          let floorCorrectionPivot = prevImg?.floorCorrectionPivot ?? null;
          let floorCorrectionWarning = prevImg?.floorCorrectionWarning;


          let analysisResult: AnalysisResult[] = [];
          if (viewName === 'Anterior') analysisResult = analyzeAnterior(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined));
          else if (viewName === 'Posterior') analysisResult = analyzePosterior(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined));
          else if (viewName === 'Left' || viewName === 'Right') analysisResult = analyzeLateral(rotateLandmarks(landmarks, floorCorrectionDeg, floorCorrectionPivot || undefined), viewName, cmPerPx, settings.unit, rotateCustomLandmarks(nextCustom, floorCorrectionDeg, floorCorrectionPivot || undefined), { omitPelvicTilt: isOptOut });

          const analysisObject = toAnalysisDataObject(analysisResult);

          setAnalysisData(prev => ({ ...prev, [viewName]: analysisObject }));
          setCapturedImages(prev => ({
            ...prev,
            [viewName]: {
              ...prev[viewName]!,
              landmarks,
              rawLandmarks,
              status: 'complete',
              cmPerPx,
              custom: nextCustom,
              pelvisPinsMeta: nextMeta,
              floorCorrectionDeg,
              floorCorrectionSource,
              floorCorrectionConfidence,
              floorCorrectionPivot,
              floorCorrectionWarning,
              ...(isOptOut ? { pelvisPinsOptOut: true } : {})
            }
          }));
          if (!currentView) setCurrentView(viewName);
          if (mode.mode === 'self-assessment') {
            trackPhotoUploadSuccess(viewName);
          }
        }

      } catch (err) {
        logger.error(`Error processing ${view}:`, err);
        const message = err instanceof Error ? err.message : 'Unknown error';
        if (mode.mode === 'self-assessment') {
          trackPhotoUploadError(view, message, compressedImageDataUrl || imageDataUrl);
        }
        showToast(`Error: ${message}`, 'error', undefined, false);
        if (view !== 'Auto-detect') {
          setCapturedImages(prev => ({ ...prev, [view]: { ...prev[view], status: 'failed' } }));
        }
      } finally {
        inFlightByViewRef.current.delete(view);
      }
    }

    isProcessingRef.current = false;
    // Check for more items in the queue and continue processing.
    if (analysisQueueRef.current.length > 0) {
      setTimeout(processQueue, 0);
    }
  }, [poseLandmarkerImage, settings.clientHeightCM, settings.unit, showToast, capturedImages, currentView, captureSettings.visibilityThreshold, mode]);

  const queueImageForProcessing = useCallback((
    imageDataUrl: string,
    view: CaptureSequence | 'Auto-detect',
    options?: { resetToInitialDetection?: boolean; captureSource?: CaptureSource }
  ) => {
    logger.debug('🔵 [POSTURE] Assigning picture to view', {
      category: 'ui',
      view,
      imageDataLength: imageDataUrl.length,
      timestamp: new Date().toISOString(),
    });
    if (settings.clientHeightCM <= 0) {
      showToast('Enter client height first.', 'error', undefined, false);
      return;
    }
    // Only check in-flight for specific views, not for 'Auto-detect'
    if (view !== 'Auto-detect' && inFlightByViewRef.current.has(view)) {
      showToast(`${view}: already processing.`, 'info', undefined, false);
      return;
    }

    analysisQueueRef.current.push({
      imageDataUrl,
      view,
      resetToInitialDetection: options?.resetToInitialDetection === true,
    });

    if (view !== 'Auto-detect') {
      setCapturedImages(prev => ({
        ...prev,
        [view]: {
          ...(prev[view] || {}),
          imageData: imageDataUrl,
          status: 'queued',
          // New frames carry their source; re-analysis of an existing image keeps the old one.
          ...(options?.captureSource ? { captureSource: options.captureSource } : {}),
          ...(options?.resetToInitialDetection === true ? { custom: {}, pelvisPinsMeta: {} } : {}),
        }
      }));
      if (!currentView) setCurrentView(view);
    }

    inFlightByViewRef.current.add(view);
    processQueue();
  }, [processQueue, settings.clientHeightCM, showToast, currentView]);

  // Remote Assessment "Review & save": load the stills handed over from the workbench tab.
  const handleRemoteHandoffError = useCallback((message: string) => showToast(message, 'error'), [showToast]);
  useRemotePostureHandoff({
    search: location.search,
    ready: !!poseLandmarkerImage,
    clientId: client?.id,
    queueImage: queueImageForProcessing,
    onError: handleRemoteHandoffError,
  });

  // Reanalyze function - must be defined AFTER queueImageForProcessing
  const handleReanalyze = useCallback(async () => {
    if (!poseLandmarkerImage) return;

    let count = 0;
    for (const view of VIEW_SEQUENCE) {
      const img = capturedImages[view];
      if (img && img.imageData) {
        // Queue for reprocessing
        queueImageForProcessing(img.imageData, view, { resetToInitialDetection: true });
        count++;
      }
    }

    if (count > 0) {
      showToast(`Reanalyzing ${count} images with new settings...`, 'info', undefined, false);
    } else {
      showToast('No images to reanalyze.', 'warning', undefined, false);
    }
  }, [capturedImages, queueImageForProcessing, poseLandmarkerImage, showToast]);

  // Track previous model settings to detect changes requiring reload
  const prevModelSettings = useRef({
    quality: captureSettings.modelQuality,
    mode: captureSettings.processingMode
  });

  // Initialize vision (and re-init if model settings change)
  useEffect(() => {
    let initialized = false;

    const initialize = async () => {
      if (initialized) return;
      initialized = true;

      // Only reload if settings actually changed or first load
      const settingsChanged =
        prevModelSettings.current.quality !== captureSettings.modelQuality ||
        prevModelSettings.current.mode !== captureSettings.processingMode;

      if (!settingsChanged && poseLandmarkerImage) return; // Already loaded and no change

      try {
        setIsLoading(true);
        // Clean up old instances if they exist (MediaPipe might not expose a cleanup, but we replace references)

        // Update refs
        prevModelSettings.current = {
          quality: captureSettings.modelQuality,
          mode: captureSettings.processingMode
        };

        // Initialize both IMAGE and VIDEO mode landmarkers
        // Note: We currently only support changing quality for now, delegate is hardcoded in createPoseLandmarker but we should update it
        // ideally mediaPipeService should accept options.
        // For now, getModelUrl is updated. 

        // TODO: Update mediaPipeService to accept delegate and model path dynamically
        // Since createPoseLandmarker uses hardcoded MODEL_FULL/INT8 inside, 
        // we need to update mediaPipeService to respect getModelUrl(quality).
        // But mediaPipeService exports initPoseLandmarkerForImage which calls initializeLandmarker with hardcoded params.
        // I need to update mediaPipeService to be more flexible first.

        // Since I updated mediaPipeService but not the init functions fully, let's assume defaults for now 
        // and just let the user reload the page if they want model changes until I refactor service fully.
        // But the user asked to change parameters.

        const imageLandmarker = await initPoseLandmarkerForImage((text, sub) => {
          setLoaderText(text);
          setLoaderSubText(sub);
        });
        
        setPoseLandmarkerImage(imageLandmarker);
        // Note: Video landmarker will be initialized lazily when live capture is started
        // setPoseLandmarkerVideo(videoLandmarker);
        showToast('AI Vision Engine Ready', 'success', 'AI Vision Engine Ready', false);
      } catch (e) {
        logger.error('Vision init failed', e);
        setLoaderText('Failed to initialize AI Vision');
        const message = e instanceof Error ? e.message : 'Check console for details.';
        setLoaderSubText(message);
        showToast('Vision init failed. See console.', 'error', undefined, false);
      } finally {
        setIsLoading(false);
      }
    };
    initialize();
  }, []); // Empty dependency array for now as full reload support requires service update. 
  // The "Reload Required" message in UI is correct for now.
  // I will stick to adding the Reanalyze button for thresholds which works instantly.

  useEffect(() => {
    if (captureHandlers?.isCameraActive && !poseLandmarkerVideo && !isLoading) {
      const initVideo = async () => {
        try {
          // Note: We don't set global isLoading to true here as we don't want to block the whole UI
          // but we can show progress in the toast or footer.
          const landmarkerVideo = await initPoseLandmarkerForVideo((text, sub) => {
            logger.info(`📽️ [Vision] Lazy Video Init: ${text} - ${sub}`);
          });
          setPoseLandmarkerVideo(landmarkerVideo);
          showToast('AI Video Tracking Ready', 'success', undefined, true);
        } catch (err) {
          logger.error('Video Vision init failed', err);
        }
      };
      initVideo();
    }
  }, [captureHandlers?.isCameraActive, poseLandmarkerVideo, isLoading, showToast]);

  useEffect(() => {
    const newModel = tryBuild3DModel(capturedImages);
    if (newModel) {
      setModel3D(newModel);
    }
  }, [capturedImages]);

  // Handle scroll to toggle floating toolbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 200; // Start floating after scrolling 200px
      setIsToolbarFloating(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Measure available height for mobile split so Pic stays pinned and only analysis scrolls.
  useEffect(() => {
    const isAnyComplete = Object.values(analysisData).some(d => d && Object.keys(d).length > 0);
    const hasContent = experimentalFeaturesEnabled || isAnyComplete;
    const split =
      isNarrowViewport &&
      showAnalysisPanel &&
      hasContent &&
      activeTab === 'assessment';
    if (!split) {
      setMobileSplitHeight(undefined);
      return;
    }
    const bottomReserve =
      mode.mode === 'self-assessment'
        ? 0
        : isChromeHidden
          ? 0
          : MOBILE_BOTTOM_NAV_HEIGHT_PX;
    const compute = () => {
      const el = mobileSplitRef.current;
      if (!el) return;
      // When flex chain handles viewport filling (self-assessment or narrow mobile split)
      if (mode.mode === 'self-assessment' || (isNarrowViewport && useMobileSplit)) {
        setMobileSplitHeight(undefined);
        return;
      }
      const top = el.getBoundingClientRect().top;
      const h = Math.max(360, Math.round(window.innerHeight - top - bottomReserve));
      setMobileSplitHeight(h);
    };
    const raf = requestAnimationFrame(compute);
    window.addEventListener('resize', compute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', compute);
    };
  }, [isNarrowViewport, windowHeight, analysisData, showAnalysisPanel, experimentalFeaturesEnabled, activeTab, isChromeHidden, mode.mode]);

  useEffect(() => {
    if (analysisData && Object.keys(analysisData).length > 0) {
      const clientSex = (client?.sex === 'Male' || client?.sex === 'Female') ? client.sex : 'Unknown';
      classifyPosture(analysisData, clientSex as any, classifyOptions).then(res => setClassification(res));
    } else {
      setClassification(null);
    }
  }, [analysisData, client?.sex, classifyOptions]);

  const resetApp = () => {
    clearFullscreenEditHistory();
    setCapturedImages({ Anterior: null, Posterior: null, Left: null, Right: null });
    setAnalysisData({});
    setModel3D(null);
    setCurrentView(null);
    setPelvis3d(null);
    setUnassignedImages([]);
    analysisQueueRef.current = [];
    isProcessingRef.current = false;
    inFlightByViewRef.current.clear();
    setSaveStatus('idle');
    setSelectedAnalysisId(null);
    // Clear draft when resetting
    if (client?.id) {
      clearDraft(client.id);
    } else {
      clearDraft(null);
    }
    setHasUnsavedDraft(false);
    setPractitionerObservations({});
    showToast('Application reset.', 'info', undefined, false);
  };

  const handleLoadAnalysis = useCallback(async (analysisId: string, options?: { silent?: boolean }) => {
    if (!mode.canLoad) return;
    if (!client?.id || !user?.uid) return;

    const currentClientId = client.id;

    try {
      const analysisToLoad = await getPostureAnalysis(currentClientId, analysisId);

      // Verify client hasn't changed during load
      if (client?.id !== currentClientId) {
        logger.warn('Client changed during analysis load, aborting');
        return;
      }

      // Double-check the analysis belongs to the current client
      const analysisClientId = (analysisToLoad as any)?.clientId;
      if (analysisClientId && analysisClientId !== currentClientId) {
        logger.error('SECURITY: Attempted to load analysis from different client!', {
          currentClientId,
          analysisClientId,
          analysisId
        });
        showToast('Cannot load analysis from different client.', 'error', undefined, false);
        return;
      }

      if (!analysisToLoad) {
        showToast('Analysis not found.', 'error', undefined, false);
        return;
      }

      // Reset first to clear current state (without toast)
      clearFullscreenEditHistory();
      setCapturedImages({ Anterior: null, Posterior: null, Left: null, Right: null });
      setAnalysisData({});
      setModel3D(null);
      setCurrentView(null);
      setUnassignedImages([]);
      setSaveStatus('idle');

      // Update settings
      if (analysisToLoad.clientHeight) {
        setSettings(prev => ({
          ...prev,
          clientHeightCM: analysisToLoad.clientHeight,
          unit: analysisToLoad.unit || 'cm'
        }));
      }

      // Reconstruct captured images
      const newCapturedImages: Record<CaptureSequence, CapturedImage | null> = {
        Anterior: null, Posterior: null, Left: null, Right: null
      };

      if (analysisToLoad.captured) {
        Object.entries(analysisToLoad.captured).forEach(([key, val]: [string, any]) => {
          if (VIEW_SEQUENCE.includes(key as CaptureSequence)) {
            newCapturedImages[key as CaptureSequence] = {
              imageData: val.imageData,
              landmarks: val.landmarks,
              cmPerPx: val.cmPerPx,
              custom: val.custom,
              status: 'complete',
              ...pickManualFloorCorrection(val),
              ...(val.pelvisPinsOptOut ? { pelvisPinsOptOut: true } : {}),
            };
          }
        });
      }

      // Re-evaluate using the central pipeline, with the manual floor correction saved on each view
      const evalResult = evaluateFullPostureAnalysis({
        captured: newCapturedImages,
        clientHeightCM: analysisToLoad.clientHeight || settings.clientHeightCM,
        unit: analysisToLoad.unit || settings.unit,
        clientSex: (client?.sex === 'Male' || client?.sex === 'Female') ? client.sex : 'Unknown',
        strategyId: effectivePostureStrategyId,
      });

      setCapturedImages(evalResult.captured as Record<CaptureSequence, CapturedImage | null>);
      setAnalysisData(evalResult.analysis);
      if (evalResult.classification) {
        setClassification(evalResult.classification);
      }

      // Set active view to first available (respecting experimental: hide Posterior when off)
      const effectiveSeq = getEffectiveViewSequence(experimentalFeaturesEnabled);
      const firstAvailable = effectiveSeq.find(v => newCapturedImages[v]?.status === 'complete');
      if (firstAvailable) {
        setCurrentView(firstAvailable);
      }

      setSelectedAnalysisId(analysisId);
      setActiveTab('assessment');
      // Clear draft when loading a saved analysis
      clearDraft(client.id);
      setHasUnsavedDraft(false);
      if (!options?.silent) {
        showToast('Analysis loaded.', 'success', undefined, false);
      }
    } catch (error) {
      logger.error('Error loading analysis:', error);
      if (!options?.silent) {
        showToast('Failed to load analysis.', 'error', undefined, false);
      }
    }
  }, [
    mode.canLoad,
    client,
    user?.uid,
    clearFullscreenEditHistory,
    settings.clientHeightCM,
    settings.unit,
    effectivePostureStrategyId,
    experimentalFeaturesEnabled,
    showToast,
  ]);

  const [searchParams] = useSearchParams();
  const autoLoadAnalysisId = searchParams.get('analysisId');
  const hasAutoLoadedRef = useRef(false);
  const hasAutoLoadedLatestRef = useRef(false);

  // Auto-load analysis if analysisId is in search params
  useEffect(() => {
    if (autoLoadAnalysisId && client?.id && !hasAutoLoadedRef.current && !isLoading && !isClientLoading) {
      logger.debug('Auto-loading analysis from URL param:', autoLoadAnalysisId);
      hasAutoLoadedRef.current = true;
      hasAutoLoadedLatestRef.current = true;
      handleLoadAnalysis(autoLoadAnalysisId);
    }
  }, [autoLoadAnalysisId, client?.id, isLoading, isClientLoading, handleLoadAnalysis]);

  useEffect(() => {
    hasAutoLoadedLatestRef.current = false;
  }, [client?.id]);

  // Default to the client's latest saved analysis (by date) when opening posture analysis.
  // Never when Remote Assessment handed over stills: they belong in a new analysis.
  const isRemoteHandoff = isRemoteHandoffSearch(location.search);
  useEffect(() => {
    const latestId = latestAnalysisToAutoLoad({
      autoLoadAnalysisId,
      clientId: client?.id,
      canLoad: mode.canLoad,
      draftCheckResolved,
      draftWasRestored,
      alreadyAutoLoaded: hasAutoLoadedLatestRef.current,
      selectedAnalysisId,
      isLoading,
      isClientLoading,
      priorAnalysisIds: priorAnalyses.map((analysis) => analysis.id),
      hasCurrentWork: Object.values(capturedImages).some((img) => img?.status === 'complete' || img?.imageData),
      isRemoteHandoff,
    });
    if (!latestId) return;

    hasAutoLoadedLatestRef.current = true;
    logger.debug('Auto-loading latest posture analysis for client:', { clientId: client?.id, latestId });
    void handleLoadAnalysis(latestId, { silent: true });
  }, [
    autoLoadAnalysisId,
    isRemoteHandoff,
    client?.id,
    mode.canLoad,
    draftCheckResolved,
    draftWasRestored,
    selectedAnalysisId,
    isLoading,
    isClientLoading,
    priorAnalyses,
    capturedImages,
    handleLoadAnalysis,
  ]);

  const deleteCurrentView = () => {
    if (!currentView) return;
    if (mode.mode === 'self-assessment') {
      trackPhotoRemoved(currentView);
    }
    clearFullscreenEditHistory();
    const viewToDelete = currentView;
    setCapturedImages(prev => ({ ...prev, [viewToDelete]: null }));
    setAnalysisData(prev => {
      const newData = { ...prev };
      delete newData[viewToDelete];
      return newData;
    });
    const effectiveSeq = getEffectiveViewSequence(experimentalFeaturesEnabled);
    const nextView = effectiveSeq.find(v => v !== viewToDelete && capturedImages[v]?.status === 'complete') || null;
    setCurrentView(nextView);
    showToast(`${viewToDelete} deleted.`, 'info', undefined, false);
  };

  // When experimental turns off and currentView is Posterior, switch to first available view
  useEffect(() => {
    if (experimentalFeaturesEnabled || currentView !== 'Posterior') return;
    const effectiveSeq = getEffectiveViewSequence(false);
    const firstAvailable = effectiveSeq.find(v => capturedImages[v]?.status === 'complete');
    setCurrentView(firstAvailable ?? 'Anterior');
  }, [experimentalFeaturesEnabled, currentView, capturedImages]);

  const handleSetCustomLandmark = (
    view: CaptureSequence,
    point: { x: number; y: number },
    type: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS'
  ) => {
    const img = capturedImages[view];
    if (!img?.landmarks || !img.cmPerPx) return;

    const normalizedType = normalizePelvisPinTypeByHipSide(
      view,
      type as PelvisPinType,
      point,
      img.landmarks
    );
    const key = normalizedType as Pelvis2DKey;

    const prevPinPlacement = (img.custom as any)?.[normalizedType] as { x: number; y: number } | undefined;
    if (prevPinPlacement && prevPinPlacement.x === point.x && prevPinPlacement.y === point.y) {
      return;
    }

    let updatedPelvisOut: PelvisLandmarks3D | null = null;
    let analysisPatch: {
      view: CaptureSequence;
      nextLm: Landmark[];
      custom: Record<string, unknown>;
      cmPerPx: number;
    } | null = null;

    flushSync(() => {
      setCapturedImages((prev) => {
        const im = prev[view];
        if (!im?.landmarks || !im.cmPerPx) return prev;

        const nextLm = im.landmarks;

        const frame = computeViewFrame(view, nextLm, im.cmPerPx);
        if (!frame) return prev;

        const capturedPayload = {
          ...(prev.Anterior?.landmarks && prev.Anterior.cmPerPx
            ? { Anterior: { landmarks: view === 'Anterior' ? nextLm : prev.Anterior.landmarks, cmPerPx: prev.Anterior.cmPerPx } }
            : {}),
          ...(prev.Posterior?.landmarks && prev.Posterior.cmPerPx
            ? { Posterior: { landmarks: view === 'Posterior' ? nextLm : prev.Posterior.landmarks, cmPerPx: prev.Posterior.cmPerPx } }
            : {}),
          ...(prev.Left?.landmarks && prev.Left.cmPerPx
            ? { Left: { landmarks: view === 'Left' ? nextLm : prev.Left.landmarks, cmPerPx: prev.Left.cmPerPx } }
            : {}),
          ...(prev.Right?.landmarks && prev.Right.cmPerPx
            ? { Right: { landmarks: view === 'Right' ? nextLm : prev.Right.landmarks, cmPerPx: prev.Right.cmPerPx } }
            : {}),
        } as any;

        const base =
          pelvis3dRef.current ??
          seedPelvis3DFromViews({ current: pelvis3dRef.current, captured: capturedPayload });
        const updatedPelvis = updatePelvis3DFrom2D(base, view, key, point, frame);
        updatedPelvisOut = updatedPelvis;
        pelvis3dRef.current = updatedPelvis;

        const now = new Date().toISOString();
        const custom = { ...(im.custom || {}), [normalizedType]: point } as any;
        const pelvisPinsMeta: PelvisPinsMeta = {
          ...(im.pelvisPinsMeta || {}),
          [normalizedType]: { source: 'user', confidence: 1, updatedAt: now },
        };

        analysisPatch = { view, nextLm, custom, cmPerPx: im.cmPerPx };

        // Placing a pin re-opts the view back into pelvic-tilt measurement.
        return { ...prev, [view]: { ...im, custom, pelvisPinsMeta, pelvisPinsOptOut: false } };
      });
    });

    if (updatedPelvisOut) {
      setPelvis3d(updatedPelvisOut);
      applyProjectedPelvisToCapturedImages(updatedPelvisOut);
    }

    if (analysisPatch) {
      const { view: v, nextLm, custom, cmPerPx } = analysisPatch;
      const deg = capturedImages[v]?.floorCorrectionDeg ?? 0;
      let rows: AnalysisResult[] = [];
      if (v === 'Anterior') {
        rows = analyzeAnterior(rotateLandmarks(nextLm, deg), v, cmPerPx, settings.unit, rotateCustomLandmarks(custom as any, deg));
      } else if (v === 'Posterior') {
        rows = analyzePosterior(rotateLandmarks(nextLm, deg), v, cmPerPx, settings.unit, rotateCustomLandmarks(custom as any, deg));
      } else if (v === 'Left' || v === 'Right') {
        // Pin was just placed in this view → measure pelvic tilt (omit = false).
        rows = analyzeLateral(rotateLandmarks(nextLm, deg), v, cmPerPx, settings.unit, rotateCustomLandmarks(custom as any, deg), {
          omitPelvicTilt: shouldOmitPelvicTilt(mode, { pelvisPinsOptOut: false }),
        });
      }
      if (rows.length > 0) {
        setAnalysisData((prev) => ({ ...prev, [v]: toAnalysisDataObject(rows) }));
      }
    }

    // Recompute lateral metrics in real-time (pelvic tilt depends on ASIS/PSIS projections)
    const pelvisAfterPin = updatedPelvisOut;
    if (pelvisAfterPin) {
      (['Left', 'Right'] as const).forEach((v) => {
        const vImg =
          analysisPatch && v === analysisPatch.view
            ? {
                landmarks: analysisPatch.nextLm,
                cmPerPx: analysisPatch.cmPerPx,
                custom: analysisPatch.custom as CapturedImageLocal['custom'],
              }
            : capturedImages[v];
        if (!vImg?.landmarks || !vImg.cmPerPx) return;
        const vFrame = computeViewFrame(v, vImg.landmarks, vImg.cmPerPx);
        if (!vFrame) return;
        const projected = projectPelvis3DToCustomByView(pelvisAfterPin, { [v]: vFrame } as any)[v];
        const customMerged = { ...(vImg.custom || {}), ...(projected || {}) };
        const deg = capturedImages[v]?.floorCorrectionDeg ?? 0;
        const rows = analyzeLateral(rotateLandmarks(vImg.landmarks, deg), v, vImg.cmPerPx, settings.unit, rotateCustomLandmarks(customMerged, deg), {
          omitPelvicTilt: shouldOmitPelvicTilt(mode, capturedImages[v]),
        });
        const analysisObject = toAnalysisDataObject(rows);
        setAnalysisData((prev) => ({ ...prev, [v]: analysisObject }));
      });
    }
  };

  const handleTranslatePelvisRedLine = useCallback(
    (view: CaptureSequence, delta: { dx: number; dy: number }) => {
      if (delta.dx === 0 && delta.dy === 0) return;
      const im0 = capturedImagesRef.current[view];
      if (!im0?.landmarks || !im0.cmPerPx || !im0.custom) return;
      const lineKeys = getPelvisRedLinePinKeys(view, im0.custom);
      if (!lineKeys) return;
      const [lk1, lk2] = lineKeys;
      const lp1 = im0.custom[lk1];
      const lp2 = im0.custom[lk2];
      if (!lp1 || !lp2) return;

      let updatedPelvisOut: PelvisLandmarks3D | null = null;
      const lateralPatch: Partial<Record<'Left' | 'Right', NonNullable<AnalysisData['Left']>>> = {};
      const translateOutcome: {
        apRecalc: { view: CaptureSequence; rows: AnalysisResult[] } | null;
      } = { apRecalc: null };

      flushSync(() => {
        setCapturedImages((prev) => {
          const im = prev[view];
          if (!im?.landmarks || !im.cmPerPx || !im.custom) return prev;

          const keys = getPelvisRedLinePinKeys(view, im.custom);
          if (!keys) return prev;
          const [k1, k2] = keys;
          const p1 = im.custom[k1];
          const p2 = im.custom[k2];
          if (!p1 || !p2) return prev;

          const np1 = { x: p1.x + delta.dx, y: p1.y + delta.dy };
          const np2 = { x: p2.x + delta.dx, y: p2.y + delta.dy };

          const nextLm = im.landmarks;
          const frame = computeViewFrame(view, nextLm, im.cmPerPx);
          if (!frame) return prev;

          const capturedPayload = {
            ...(prev.Anterior?.landmarks && prev.Anterior.cmPerPx
              ? {
                  Anterior: {
                    landmarks: view === 'Anterior' ? nextLm : prev.Anterior.landmarks,
                    cmPerPx: prev.Anterior.cmPerPx,
                  },
                }
              : {}),
            ...(prev.Posterior?.landmarks && prev.Posterior.cmPerPx
              ? {
                  Posterior: {
                    landmarks: view === 'Posterior' ? nextLm : prev.Posterior.landmarks,
                    cmPerPx: prev.Posterior.cmPerPx,
                  },
                }
              : {}),
            ...(prev.Left?.landmarks && prev.Left.cmPerPx
              ? {
                  Left: {
                    landmarks: view === 'Left' ? nextLm : prev.Left.landmarks,
                    cmPerPx: prev.Left.cmPerPx,
                  },
                }
              : {}),
            ...(prev.Right?.landmarks && prev.Right.cmPerPx
              ? {
                  Right: {
                    landmarks: view === 'Right' ? nextLm : prev.Right.landmarks,
                    cmPerPx: prev.Right.cmPerPx,
                  },
                }
              : {}),
          } as any;

          const base =
            pelvis3dRef.current ??
            seedPelvis3DFromViews({ current: pelvis3dRef.current, captured: capturedPayload });
          let updatedPelvis = updatePelvis3DFrom2D(base, view, k1 as Pelvis2DKey, np1, frame);
          updatedPelvis = updatePelvis3DFrom2D(updatedPelvis, view, k2 as Pelvis2DKey, np2, frame);
          updatedPelvisOut = updatedPelvis;
          pelvis3dRef.current = updatedPelvis;

          const now = new Date().toISOString();
          const custom = { ...(im.custom || {}), [k1]: np1, [k2]: np2 } as any;
          const pelvisPinsMeta: PelvisPinsMeta = {
            ...(im.pelvisPinsMeta || {}),
            [k1]: { source: 'user', confidence: 1, updatedAt: now },
            [k2]: { source: 'user', confidence: 1, updatedAt: now },
          };

          // Dragging the pelvis red line edits user pins → re-opt this view into pelvic tilt.
          const next = { ...prev, [view]: { ...im, custom, pelvisPinsMeta, pelvisPinsOptOut: false } };

          if (view === 'Anterior') {
            translateOutcome.apRecalc = {
              view,
              rows: analyzeAnterior(rotateLandmarks(nextLm, im.floorCorrectionDeg ?? 0), view, im.cmPerPx, settings.unit, rotateCustomLandmarks(custom, im.floorCorrectionDeg ?? 0)),
            };
          } else if (view === 'Posterior') {
            translateOutcome.apRecalc = {
              view,
              rows: analyzePosterior(rotateLandmarks(nextLm, im.floorCorrectionDeg ?? 0), view, im.cmPerPx, settings.unit, rotateCustomLandmarks(custom, im.floorCorrectionDeg ?? 0)),
            };
          } else {
            translateOutcome.apRecalc = null;
          }

          (['Left', 'Right'] as const).forEach((v) => {
            const vImg = next[v];
            if (vImg?.landmarks && vImg.cmPerPx) {
              const vFrame = computeViewFrame(v, vImg.landmarks, vImg.cmPerPx);
              if (!vFrame) return;
              const projected = projectPelvis3DToCustomByView(updatedPelvis, { [v]: vFrame } as any)[v];
              const mergedCustom = { ...(vImg.custom || {}), ...(projected || {}) };
              const deg = vImg.floorCorrectionDeg ?? 0;
              const rows = analyzeLateral(rotateLandmarks(vImg.landmarks, deg), v, vImg.cmPerPx, settings.unit, rotateCustomLandmarks(mergedCustom, deg), {
                omitPelvicTilt: shouldOmitPelvicTilt(mode, vImg as CapturedImageLocal | null),
              });
              lateralPatch[v] = toAnalysisDataObject(rows);
            }
          });

          return next;
        });
      });

      if (updatedPelvisOut) {
        setPelvis3d(updatedPelvisOut);
        applyProjectedPelvisToCapturedImages(updatedPelvisOut);
      }
      const apRecalc = translateOutcome.apRecalc;
      if (apRecalc && apRecalc.rows.length > 0) {
        const { view: apV, rows: apRows } = apRecalc;
        setAnalysisData((prev) => ({
          ...prev,
          [apV]: toAnalysisDataObject(apRows),
        }));
      }
      if (Object.keys(lateralPatch).length > 0) {
        setAnalysisData((prev) => {
          const next = { ...prev };
          (['Left', 'Right'] as const).forEach((v) => {
            const p = lateralPatch[v];
            if (p) next[v] = p;
          });
          return next;
        });
      }
    },
    [applyProjectedPelvisToCapturedImages, settings.unit]
  );

  /**
   * Opt out of pelvis pins entirely: clears every pelvis pin across all views, marks each
   * captured view `pelvisPinsOptOut: true`, and recomputes lateral analysis with the pelvic-tilt
   * row omitted. Mirrors master's behaviour.
   */
  const handleClearAllPelvisPins = useCallback(
    () => {
      const allViews: CaptureSequence[] = ['Anterior', 'Posterior', 'Left', 'Right'];

      // Detect whether anything will actually change to avoid a no-op state churn.
      let touched = false;
      const nextStateByView: Partial<Record<CaptureSequence, {
        custom: CustomLandmarksLocal;
        pelvisPinsMeta: PelvisPinsMeta;
      }>> = {};

      for (const v of allViews) {
        const img = capturedImages[v];
        if (!img) continue;
        const keys = pelvisPinKeysForView(v);
        const custom = { ...(img.custom || {}) } as CustomLandmarksLocal;
        const pelvisPinsMeta: PelvisPinsMeta = { ...(img.pelvisPinsMeta || {}) };
        for (const k of keys) {
          if ((custom as Record<string, unknown>)[k] !== undefined) touched = true;
          if (pelvisPinsMeta[k]) touched = true;
          delete (custom as Record<string, unknown>)[k];
          delete pelvisPinsMeta[k];
        }
        nextStateByView[v] = { custom, pelvisPinsMeta };
      }

      if (!touched) return;

      setCapturedImages((prev) => {
        const next = { ...prev };
        for (const v of allViews) {
          const cur = prev[v];
          if (!cur) continue;
          const upd = nextStateByView[v];
          if (!upd) continue;
          next[v] = {
            ...cur,
            custom: upd.custom as CustomLandmarksLocal,
            pelvisPinsMeta: upd.pelvisPinsMeta,
            pelvisPinsOptOut: true,
          };
        }
        return next;
      });

      // Refresh lateral analysis without pelvic tilt (anterior/posterior metrics don't depend on ASIS/PSIS).
      for (const v of ['Left', 'Right'] as const) {
        const img = capturedImages[v];
        if (!img?.landmarks || !img.cmPerPx) continue;
        const upd = nextStateByView[v];
        const custom = upd?.custom ?? {};
        const deg = img.floorCorrectionDeg ?? 0;
        const rows = analyzeLateral(rotateLandmarks(img.landmarks, deg), v, img.cmPerPx, settings.unit, rotateCustomLandmarks(custom, deg), { omitPelvicTilt: true });
        setAnalysisData((prev) => ({ ...prev, [v]: toAnalysisDataObject(rows) }));
      }
    },
    [capturedImages, settings.unit]
  );

  const handleSetPoseLandmark = useCallback((
    view: CaptureSequence,
    index: number,
    point: { x: number; y: number }
  ) => {
    const imgRef = capturedImagesRef.current[view];
    if (!imgRef?.landmarks || index < 0 || index >= imgRef.landmarks.length) return;
    const lm0 = imgRef.landmarks[index];
    if (lm0.x === point.x && lm0.y === point.y) return;

    setCapturedImages((prev) => {
      const img = prev[view];
      if (!img?.landmarks || index < 0 || index >= img.landmarks.length) return prev;
      const nextLandmarks = img.landmarks.map((lm, i) =>
        i === index ? { ...lm, x: point.x, y: point.y } : lm
      );
      return { ...prev, [view]: { ...img, landmarks: nextLandmarks } };
    });
    // Recompute analysis for this view in real time
    const img = capturedImages[view];
    if (img?.landmarks && img.cmPerPx) {
      const nextLandmarks = [...(img.landmarks || [])];
      if (index >= 0 && index < nextLandmarks.length) {
        nextLandmarks[index] = { ...nextLandmarks[index], x: point.x, y: point.y };
      }
      const deg = img.floorCorrectionDeg ?? 0;
      let rows: AnalysisResult[] = [];
      if (view === 'Anterior') {
        rows = analyzeAnterior(rotateLandmarks(nextLandmarks, deg), view, img.cmPerPx, settings.unit, rotateCustomLandmarks(img.custom, deg));
      } else if (view === 'Posterior') {
        rows = analyzePosterior(rotateLandmarks(nextLandmarks, deg), view, img.cmPerPx, settings.unit, rotateCustomLandmarks(img.custom, deg));
      } else if (view === 'Left' || view === 'Right') {
        rows = analyzeLateral(rotateLandmarks(nextLandmarks, deg), view, img.cmPerPx, settings.unit, rotateCustomLandmarks(img.custom, deg), {
          omitPelvicTilt: shouldOmitPelvicTilt(mode, img),
        });
      }
      if (rows.length > 0) {
        const analysisObject = toAnalysisDataObject(rows);
        setAnalysisData((prev) => ({ ...prev, [view]: analysisObject }));
      }
      
      // REFINEMENT: Re-seed Pelvis 3D if landmarks move (affects view framing)
      const base = pelvis3d ?? seedPelvis3DFromViews({
        current: pelvis3d,
        captured: {
          ...(view === 'Anterior' ? { Anterior: { landmarks: nextLandmarks, cmPerPx: img.cmPerPx } } : (capturedImages.Anterior?.landmarks && capturedImages.Anterior.cmPerPx ? { Anterior: { landmarks: capturedImages.Anterior.landmarks, cmPerPx: capturedImages.Anterior.cmPerPx } } : {})),
          ...(view === 'Posterior' ? { Posterior: { landmarks: nextLandmarks, cmPerPx: img.cmPerPx } } : (capturedImages.Posterior?.landmarks && capturedImages.Posterior.cmPerPx ? { Posterior: { landmarks: capturedImages.Posterior.landmarks, cmPerPx: capturedImages.Posterior.cmPerPx } } : {})),
          ...(view === 'Left' ? { Left: { landmarks: nextLandmarks, cmPerPx: img.cmPerPx } } : (capturedImages.Left?.landmarks && capturedImages.Left.cmPerPx ? { Left: { landmarks: capturedImages.Left.landmarks, cmPerPx: capturedImages.Left.cmPerPx } } : {})),
          ...(view === 'Right' ? { Right: { landmarks: nextLandmarks, cmPerPx: img.cmPerPx } } : (capturedImages.Right?.landmarks && capturedImages.Right.cmPerPx ? { Right: { landmarks: capturedImages.Right.landmarks, cmPerPx: capturedImages.Right.cmPerPx } } : {})),
        } as any
      });
      if (base) {
        setPelvis3d(base);
        applyProjectedPelvisToCapturedImages(base);
      }
    }
  }, [capturedImages, settings.unit, pelvis3d, applyProjectedPelvisToCapturedImages]);

  /** Manual plumb (4B): updates line angle for display only — gauge formulas unchanged. */
  const handleManualPlumbUpdate = useCallback((
    view: CaptureSequence,
    options: {
      manualFloorPoints?: [{ x: number; y: number }, { x: number; y: number }];
      manualCorrectionDeg?: number;
      plControlPoint?: { x: number; y: number };
      resetToAuto?: boolean;
    }
  ) => {
    const current = capturedImagesRef.current[view];
    if (!current) return;

    if (options.resetToAuto) {
      setCapturedImages((prev) => {
        const img = prev[view];
        if (!img) return prev;
        return {
          ...prev,
          [view]: {
            ...img,
            floorCorrectionDeg: 0,
            floorCorrectionPivot: null,
            floorCorrectionSource: 'none',
            floorCorrectionConfidence: 0,
            floorCorrectionWarning: undefined,
            manualFloorPoints: undefined,
            plLandmarks: undefined,
          },
        };
      });
      return;
    }

    let nextCorrectionDeg = current.floorCorrectionDeg ?? 0;
    let nextSource = current.floorCorrectionSource ?? 'none';
    let nextConfidence = current.floorCorrectionConfidence ?? 0;
    let nextWarning = current.floorCorrectionWarning;
    let nextManualFloorPoints = current.manualFloorPoints;
    let nextPlLandmarks = current.plLandmarks;
    let nextPivot = current.floorCorrectionPivot ?? null;

    if (options.manualFloorPoints) {
      const manual = resolveManualFloorCorrectionFromPoints(
        options.manualFloorPoints[0],
        options.manualFloorPoints[1]
      );
      if (!manual) return;
      nextCorrectionDeg = manual.correctionDeg;
      nextSource = manual.source;
      nextConfidence = manual.confidence;
      nextWarning = undefined;
      nextPivot = manual.pivot;
      nextManualFloorPoints = options.manualFloorPoints;
    }
    if (typeof options.manualCorrectionDeg === 'number') {
      nextCorrectionDeg = options.manualCorrectionDeg;
      nextSource = 'manual';
      nextConfidence = 1;
      nextWarning = undefined;
      nextPivot = current.landmarks
        ? plumbAnchor(view, current.landmarks, 1000, 1000, 0)
        : nextPivot;
    }
    if (options.plControlPoint) {
      nextPlLandmarks = {
        ...(nextPlLandmarks ?? {}),
        control: options.plControlPoint,
      };
    }

    setCapturedImages((prev) => {
      const img = prev[view];
      if (!img) return prev;
      return {
        ...prev,
        [view]: {
          ...img,
          floorCorrectionDeg: nextCorrectionDeg,
          floorCorrectionPivot: nextPivot,
          floorCorrectionSource: nextSource,
          floorCorrectionConfidence: nextConfidence,
          floorCorrectionWarning: nextWarning,
          manualFloorPoints: nextManualFloorPoints,
          plLandmarks: nextPlLandmarks,
        },
      };
    });

    if (current.landmarks) {
      const isOptOut = shouldOmitPelvicTilt(mode, current as CapturedImageLocal | null);
      let analysisResult: AnalysisResult[] = [];
      if (view === 'Anterior') analysisResult = analyzeAnterior(rotateLandmarks(current.landmarks, nextCorrectionDeg, nextPivot || undefined), view, current.cmPerPx || 0.2, settings.unit, rotateCustomLandmarks(current.custom || {}, nextCorrectionDeg, nextPivot || undefined));
      else if (view === 'Posterior') analysisResult = analyzePosterior(rotateLandmarks(current.landmarks, nextCorrectionDeg, nextPivot || undefined), view, current.cmPerPx || 0.2, settings.unit, rotateCustomLandmarks(current.custom || {}, nextCorrectionDeg, nextPivot || undefined));
      else if (view === 'Left' || view === 'Right') analysisResult = analyzeLateral(rotateLandmarks(current.landmarks, nextCorrectionDeg, nextPivot || undefined), view, current.cmPerPx || 0.2, settings.unit, rotateCustomLandmarks(current.custom || {}, nextCorrectionDeg, nextPivot || undefined), { omitPelvicTilt: isOptOut });

      const analysisObject = toAnalysisDataObject(analysisResult);
      setAnalysisData((prev) => ({ ...prev, [view]: analysisObject }));
    }
  }, [mode, settings.unit]);

  const fullscreenEditHistoryForCapture = useMemo(
    () => ({
      canUndo: fullscreenUndoStackRef.current.length > 0,
      canRedo: fullscreenRedoStackRef.current.length > 0,
      onUndo: handleFullscreenUndo,
      onRedo: handleFullscreenRedo,
    }),
    [fullscreenHistoryTick, handleFullscreenUndo, handleFullscreenRedo]
  );

  const handleSaveAnalysis = async () => {
    if (!client) {
      showToast("No Client Selected. Please select a client before saving.", 'error');
      return;
    }
    if (!user?.uid) {
      showToast("Not Authenticated. Please sign in to save analysis.", 'error');
      return;
    }
    const completedViews = Object.values(capturedImages).filter(img => img?.status === 'complete');
    if (completedViews.length === 0) {
      showToast("No Analysis to Save. Please complete at least one posture analysis view.", 'error');
      return;
    }

    setSaveStatus('saving');
    try {
      const capturedDataForDb: any = buildCapturedViewsForSave(capturedImages);

      const isUpdatingExisting = Boolean(selectedAnalysisId);
      const clientSex = (client?.sex === 'Male' || client?.sex === 'Female') ? client.sex : 'Unknown';
      const evaluateForSave = () =>
        evaluateFullPostureAnalysis({
          captured: capturedImages,
          clientHeightCM: settings.clientHeightCM,
          unit: settings.unit,
          clientSex: clientSex as any,
          strategyId: effectivePostureStrategyId,
          sopCompletionRatio: classifyOptions.strategyInput?.sopCompletionRatio,
          sopTablePosture: classifyOptions.strategyInput?.sopTablePosture,
          practitionerObservations: classifyOptions.strategyInput?.practitionerObservations,
          sopMetrics: classifyOptions.strategyInput?.sopMetrics,
        });

      if (isUpdatingExisting) {
        const {
          analysis: recalculatedAnalysis,
          classification,
        } = evaluateForSave();

        const hasLateralViews = recalculatedAnalysis['Left'] || recalculatedAnalysis['Right'];
        if (!hasLateralViews) {
          showToast('Classification requires at least one lateral view (Left or Right).', 'warning');
          setSaveStatus('idle');
          return;
        }
        if (!classification) {
          showToast('Could not determine posture pattern.', 'error');
          setSaveStatus('idle');
          return;
        }
        await updatePostureAnalysis(client.id, selectedAnalysisId!, {
          captured: capturedDataForDb,
          analysis: recalculatedAnalysis,
          classification: classification as any,
          _reanalyzedAt: new Date().toISOString(),
        });
        setAnalysisData(recalculatedAnalysis);
        setSaveStatus('saved');
        clearDraft(client.id);
        setHasUnsavedDraft(false);
        await refreshPriorAnalyses(client.id);
        // Only refresh the persisted classification badge when the edited analysis is
        // the client's latest — recomputing from an older scan would overwrite a
        // classification derived from newer data. (priorAnalyses is sorted newest-first;
        // an empty list means we can't rank, so treat the edit as latest.)
        if (priorAnalyses.length === 0 || priorAnalyses[0].id === selectedAnalysisId) {
          void refreshClientClassificationFromPosture(client.id, recalculatedAnalysis as AnalysisData);
        }
        showToast('Analysis updated!', 'success');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        // Store the pattern result too: without it the client page re-analyses the saved
        // analysis on open and writes its own results over these.
        const { classification } = evaluateForSave();
        const docId = await createPostureAnalysis(client.id, {
          clientHeight: settings.clientHeightCM,
          unit: settings.unit,
          captured: capturedDataForDb,
          analysis: analysisData,
          ...(classification ? { classification: classification as any } : {}),
        });
        setSaveStatus('saved');
        clearDraft(client.id);
        setHasUnsavedDraft(false);
        setSelectedAnalysisId(docId);
        await refreshPriorAnalyses(client.id);
        // Keep the client's roster/profile classification badge fresh (non-blocking).
        void refreshClientClassificationFromPosture(client.id, analysisData as AnalysisData);
        if (captureLacksLateralView(capturedImages)) {
          showToast(LATERAL_VIEW_REQUIRED_NOTICE, 'warning');
        }
        showToast("Analysis Saved!", 'success');

        const isOnEditingPage = location.pathname === '/posture-analysis';
        if (!isOnEditingPage) {
          setTimeout(() => {
            navigate(`/clients/${client.id}/posture-analysis/${docId}`);
          }, 1500);
        } else {
          setTimeout(() => setSaveStatus('idle'), 2000);
        }
      }

    } catch (error) {
      logger.error("Error saving analysis:", error);
      const message = error instanceof Error ? error.message : "An unknown error occurred.";
      showToast("Save Failed: " + message, 'error');
      setSaveStatus('idle');
    }
  };

  const handleExportPdf = async () => {
    const analysisToExport = {
      id: 'new-analysis',
      createdAt: new Date(),
      clientHeight: settings.clientHeightCM,
      unit: settings.unit,
      captured: capturedImages,
      analysis: analysisData,
    };

    const fullAnalysis = analysisToExport as unknown as import('@/lib/types').PostureAnalysis;

    try {
      await exportPostureAnalysisToPdf(fullAnalysis, client);
    } catch (error) {
      logger.error("Failed to export Posture Analysis PDF:", error);
      const message = error instanceof Error ? error.message : 'Could not generate PDF.';
      showToast(`Export Failed: ${message}`, 'error');
    }
  };

  if (isLoading) {
    return <Loader text={loaderText} subText={loaderSubText} />;
  }

  if (!mode.requiresAuth && settings.clientHeightCM <= 0) {
    setSettings((prev) => {
      const next = { ...prev, clientHeightCM: DEFAULT_ADULT_HEIGHT_CM };
      savePostureSettingsToLocalStorage(next);
      return next;
    });
  }

  if (isClientLoading && mode.requiresAuth) {
    return (
      <div className="flex min-h-screen flex-col items-start bg-background p-4">
        <p className="text-muted-foreground">Loading client...</p>
      </div>
    );
  }

  if (!client && mode.requiresAuth) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-8 py-4">
          <div className="flex items-start">
            <Card className="max-w-lg w-full">
              <CardHeader>
                <div className="bg-primary/10 text-primary p-3 rounded-full mb-4 w-fit">
                  <PersonStanding className="h-8 w-8" />
                </div>
                <CardTitle>Select a Client to Begin</CardTitle>
                <CardDescription>
                  To start a new Analysis, select an existing client or create a new one.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <SelectClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <Button className="h-12" onClick={() => setIsDialogOpen(true)}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Select Existing Client
                    </Button>
                  </SelectClientDialog>
                  <Button asChild variant="secondary" className="h-12">
                    <Link to="/clients/new">
                      Create New Client
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const isAnyAnalysisComplete = Object.values(analysisData).some(d => d && Object.keys(d).length > 0);
  // Same condition as the Analysis toolbar button: show tiles whenever experimental
  // features are on OR some analysis is complete.
  const hasAnyAnalysisContent = experimentalFeaturesEnabled || isAnyAnalysisComplete;

  // On md+ (≥ 768 px) the analysis tiles sit in a `1fr` right column next to the canvas.
  // Reserve a minimum width for that column so the canvas doesn't grow too wide and
  // leaves the analysis panel with at least ~240 px of readable space.
  const ANALYSIS_PANEL_MIN_WIDTH = 300;
  const ANALYSIS_GAP = 16; // matches gap-4
  const mobileSplitOptions = {
    isNarrowViewport,
    showAnalysisPanel,
    hasAnyAnalysisContent,
  };
  // With the canvas in a 1fr grid column the browser already accounts for the analysis
  // panel width via the grid layout — no manual right-side pixel reservation needed.
  const rightReservationPx = 0;
  const useMobileSplit = shouldUsePostureMobileSplit(mobileSplitOptions);
  const analysisTilesLayout = mode.mode === 'self-assessment' ? 'accordion' : postureAnalysisTilesLayout(mobileSplitOptions);
  const hasAnyCapturedImage = Object.values(capturedImages).some((img) => img != null);
  const fillMobileViewport = isNarrowViewport && (
    (mode.mode === 'self-assessment' && hasAnyCapturedImage) ||
    useMobileSplit
  );
  /** Desktop and narrow viewports: the picture fits the space it is given. */
  const fillDesktopViewport = mode.mode !== 'self-assessment' && !isNarrowViewport;
  const fitPhotoToViewport =
    fillDesktopViewport || fillMobileViewport || (isNarrowViewport && mode.mode !== 'self-assessment');
  const selfAssessmentMobileShell = useSelfAssessmentMobileCaptureShell(mode, isNarrowViewport);
  const showMergedSaMobileBar = selfAssessmentMobileShell && hasAnyCapturedImage;
  const shouldCapMobileSplitCanvas = shouldApplyMobileSplitCanvasCap({
    useMobileSplit,
    isSelfAssessment: mode.mode === 'self-assessment',
    hasAnyCapturedImage,
    unassignedCount: unassignedImages.length,
  });
  const mobileSplitCanvasCapPx = computeMobileSplitCanvasCapPx({
    useMobileSplit,
    shouldCapCanvas: shouldCapMobileSplitCanvas,
    windowHeight,
    isChromeHidden,
    isPractitioner: mode.mode !== 'self-assessment',
  });
  const captureButtonLabel = captureHandlers
    ? (captureHandlers.isCameraActive
      ? (captureHandlers.nextCaptureView ? `Capture ${captureHandlers.nextCaptureView}` : 'Finish capture')
      : 'Start capture')
    : 'Capture';

  const currentAnalysisForComparison = {
    id: 'current',
    createdAt: new Date(),
    captured: capturedImages
  };

  return (
    <div className={cn(
      'flex flex-col gap-4 relative',
      fitPhotoToViewport && isNarrowViewport && 'h-full max-h-full min-h-0 flex-1 gap-2 overflow-hidden',
      fillDesktopViewport && 'h-full max-h-[calc(100dvh-4.75rem)] min-h-0 flex-1 gap-2 overflow-hidden',
      mode.mode === 'self-assessment' && 'light',
      mode.mode === 'self-assessment' && isNarrowViewport && !fillMobileViewport && 'touch-pan-y',
    )}>
      <PostureBottomChromeStackSync active={useMobileSplit && !isChromeHidden && mode.requiresAuth} />
      <div className={cn(
        'flex flex-col',
        mode.mode === 'self-assessment' ? 'bg-transparent' : 'bg-background',
        (fitPhotoToViewport) && 'min-h-0 flex-1 h-full',
      )}>
        <div className={cn(
          'w-full',
          mode.mode === 'self-assessment' ? 'p-0' : 'px-1 sm:px-4 py-0.5 sm:py-2',
          fillDesktopViewport && 'px-0 py-0',
          (fitPhotoToViewport) && 'flex min-h-0 flex-1 flex-col',
          fillMobileViewport && 'py-1 sm:pb-2',
        )}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className={cn('w-full', fitPhotoToViewport && 'flex min-h-0 flex-1 flex-col')}>
            {/* Top Toolbar (Practitioner mode) */}
            {mode.mode !== 'self-assessment' && (
              <div
                className={cn(
                  'flex w-full min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto overflow-y-hidden scrollbar-hide [&>*]:shrink-0',
                  showMergedSaMobileBar
                    ? 'sticky top-0 z-40 -mx-3 mb-1 border-b border-line bg-surface px-3 py-1.5 text-fg touch-pan-xy'
                    : fillDesktopViewport
                    ? 'mb-1 shrink-0'
                    : 'mb-1 md:mb-4'
                )}
              >
                {showMergedSaMobileBar && (
                  <a
                    href="/self-assessment"
                    className="inline-flex shrink-0 items-center justify-center min-h-8 rounded-lg bg-primary px-3 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Exit check
                  </a>
                )}
                {(mode.canLoad || (mode.canCompare && experimentalFeaturesEnabled)) && (
                  <>
                <TabsList className="bg-transparent p-0 h-auto inline-flex flex-nowrap shrink-0">
                  {mode.canLoad && (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-transparent hover:bg-muted/50"
                      aria-label="Select assessment"
                    >
                      <LayoutDashboard className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">
                        {selectedAnalysisId && priorAnalyses.find(a => a.id === selectedAnalysisId)
                          ? priorAnalyses.find(a => a.id === selectedAnalysisId)?.label
                          : 'New Assessment'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50 hidden sm:block" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
                      <DropdownMenuItem onClick={() => {
                        resetApp();
                        setActiveTab('assessment');
                      }}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        New Assessment
                      </DropdownMenuItem>
                      {priorAnalyses.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="flex items-center gap-2">
                            <History className="h-4 w-4" />
                            Previous Assessments
                          </DropdownMenuLabel>
                          {priorAnalyses.map((analysis) => (
                            <DropdownMenuItem
                              key={analysis.id}
                              onClick={() => handleLoadAnalysis(analysis.id)}
                            >
                              {analysis.label}
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                  {mode.canCompare && experimentalFeaturesEnabled && (
                    <>
                      {/*
                        S7/U6: deliberately NOT disabled. A disabled trigger cannot surface its own
                        reason — tooltips do not exist on touch — so the tab stays selectable and
                        `TabsContent` explains why there is nothing to compare yet.
                      */}
                      <TabsTrigger value="compare" className="data-[state=active]:bg-accent data-[state=active]:shadow-none">
                        <History className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Compare</span>
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
                <ToolbarSeparator />
                  </>
                )}
                {mode.canShowAnalysisPanel && (experimentalFeaturesEnabled || isAnyAnalysisComplete) && !(useMobileSplit && !mode.requiresAuth) && (
                  <ToolbarButton
                    onClick={() => setAppSettings(prev => ({ ...prev, postureAnalysisPanelEnabled: !showAnalysisPanel }))}
                    className={showAnalysisPanel && activeTab !== 'assessment' ? 'bg-accent text-accent-foreground' : ''}
                    aria-pressed={showAnalysisPanel && activeTab !== 'assessment'}
                  >
                    <Activity className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                    {/*
                      U2: below `sm` the label is hidden, which left this toggle with no accessible
                      name at all. The aria-label covers that case; aria-pressed reports the state,
                      which was otherwise carried only by the accent background.
                    */}
                    <span className="hidden sm:inline">Analysis</span>
                    <span className="sr-only sm:hidden">Analysis panel</span>
                  </ToolbarButton>
                )}
                <ToolbarButton
                  onClick={() => {
                    if (captureHandlers) captureHandlers.stopCamera();
                    resetApp();
                    setActiveTab('assessment');
                  }}
                  className={cn(
                    showMergedSaMobileBar && 'h-8 w-8 px-0',
                    mode.mode === 'self-assessment' && 'text-fg hover:bg-bg',
                  )}
                  aria-label="Reset assessment"
                  title="Reset assessment"
                >
                  <RotateCcw className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                  <span className="hidden sm:inline">Reset</span>
                </ToolbarButton>

                {(showMergedSaMobileBar || (!mode.requiresAuth && onSelfAssessmentReportReady && (isAnyAnalysisComplete || Object.values(capturedImages).some(Boolean)))) && (
                  <div className="flex-1" />
                )}

                {(isAnyAnalysisComplete || (!mode.requiresAuth && onSelfAssessmentReportReady && Object.values(capturedImages).some(Boolean))) && (
                  <>
                    {mode.canSave && (
                      <SaveAnalysisButton
                        status={saveStatus}
                        isUpdate={Boolean(selectedAnalysisId)}
                        onClick={handleSaveAnalysis}
                      />
                    )}
                    {mode.canSave && selectedAnalysisId && client && (
                      <Button variant="outline" size="sm" className="h-9 sm:h-10" asChild>
                        <Link
                          to={`/clients/${client.id}/posture-analysis/${selectedAnalysisId}`}
                          className="inline-flex items-center"
                          aria-label="View analysis"
                        >
                          <ExternalLink className="h-4 w-4 sm:mr-2 shrink-0" aria-hidden />
                          <span className="hidden sm:inline">View analysis</span>
                        </Link>
                      </Button>
                    )}
                    {mode.canExportPdf && (
                      <ToolbarButton
                        onClick={handleExportPdf}
                        aria-label="Export PDF"
                        title="Export PDF"
                      >
                        <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                        <span className="hidden sm:inline">PDF</span>
                      </ToolbarButton>
                    )}
                    {!mode.requiresAuth && onSelfAssessmentReportReady && (
                      <ToolbarButton
                        isPrimary
                        className={cn(
                          showMergedSaMobileBar && 'h-8 px-2.5 text-xs',
                          isAllCaptured && 'animate-view-report-pulse'
                        )}
                        onClick={() =>
                          onSelfAssessmentReportReady({
                            id: 'self-assessment',
                            createdAt: new Date(),
                            clientHeight: settings.clientHeightCM,
                            unit: settings.unit,
                            captured: capturedImages,
                            analysis: analysisData,
                            classification,
                          } as PostureAnalysis)
                        }
                      >
                        <Activity className="h-4 w-4 sm:mr-2" />
                        <span>View your report</span>
                      </ToolbarButton>
                    )}
                  </>
                )}
              </div>
            )}

            <TabsContent value="assessment" className={cn('mt-0', fitPhotoToViewport && 'flex min-h-0 flex-1 flex-col overflow-hidden')}>
              {/* Photography Tips Drawer */}
              {captureHandlers?.isCameraActive && (
                <PhotographyTipsDrawer
                  isOpen={showTipsDrawer && (appSettings.showPostureAnalysisTips !== false)}
                  onClose={() => setShowTipsDrawer(false)}
                />
              )}

              {/* Footer Removed - Actions moved to top panel */}

              {/*
                Unified responsive layout:
                  < 768 px  (narrow mobile)  → Pattern 2: pinned Pic + segmented analysis below
                  ≥ 768 px  (tablet/desktop) → Pattern 3: canvas left column, tiles right column
              */}
              <div className={cn(mode.mode === 'self-assessment' ? 'mt-1' : fitPhotoToViewport ? 'mt-0' : 'mt-1 sm:mt-4', fitPhotoToViewport && 'mt-0 flex min-h-0 flex-1 flex-col')}>
                {/*
                  Desktop: the photo column is half the workspace and fills the
                  viewport height. The picture scales inside that box.
                  On a phone the picture fills the viewport the same way: fully
                  visible, no scroll, larger or smaller with the window.
                */}
                <div
                  ref={mobileSplitRef}
                  className={cn(
                    mode.mode === 'self-assessment'
                      ? 'flex flex-col gap-4 w-full'
                      : useMobileSplit
                        ? 'flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden'
                        : showAnalysisPanel && hasAnyAnalysisContent
                          ? cn(
                              'grid items-start grid-cols-1',
                              fillDesktopViewport
                                ? 'h-full min-h-0 items-stretch gap-3 md:grid-cols-[1fr_1fr]'
                                : 'gap-6 md:grid-cols-[minmax(0,1fr)_auto]',
                            )
                          : cn('grid items-start grid-cols-1', fillDesktopViewport ? 'h-full min-h-0 gap-4' : 'gap-4'),
                    fillMobileViewport && useMobileSplit && 'flex-1',
                  )}
                  style={
                    useMobileSplit && mobileSplitHeight
                      ? { height: `${mobileSplitHeight}px` }
                      : undefined
                  }
                >
                  {/* Canvas column – full width on mobile split & self-assessment; fills 1fr on md+ and centers the canvas */}
                  <div className={cn(
                    fitPhotoToViewport
                      ? 'flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden'
                      : useMobileSplit || mode.mode === 'self-assessment'
                        ? 'flex min-h-0 shrink-0 flex-col w-full min-w-0'
                        : 'min-w-0 w-full flex flex-col items-center md:sticky md:top-[4.5rem] md:self-start',
                  )}>
                    <CaptureModule
                      settings={settings}
                      captureSettings={captureSettings}
                      onCaptureSettingsChange={setCaptureSettings}
                      capturedImages={capturedImages}
                      currentView={currentView}
                      overlayState={overlayState}
                      onQueueImage={queueImageForProcessing}
                      onManualPlumbUpdate={handleManualPlumbUpdate}
                      onViewSelect={setCurrentView}
                      onReset={resetApp}
                      onDeleteView={deleteCurrentView}
                      onOverlayToggle={(view: CaptureSequence) => {
                        setOverlayState(prev => {
                          const currentState = prev[view]?.client ?? true;
                          return { ...prev, [view]: { ...prev[view], client: !currentState } };
                        });
                      }}
                      onRealtimeOverlayToggle={(view: CaptureSequence) => {
                        setOverlayState(prev => {
                          const currentState = prev[view]?.realtime ?? false;
                          return { ...prev, [view]: { ...prev[view], realtime: !currentState } };
                        });
                      }}
                      onSetCustomLandmark={mode.mode !== 'self-assessment' ? handleSetCustomLandmark : undefined}
                      onClearAllPelvisPins={mode.mode !== 'self-assessment' ? handleClearAllPelvisPins : undefined}
                      onTranslatePelvisRedLine={mode.mode !== 'self-assessment' ? handleTranslatePelvisRedLine : undefined}
                      onSetPoseLandmark={mode.mode !== 'self-assessment' ? handleSetPoseLandmark : undefined}
                      unassignedImages={unassignedImages}
                      setUnassignedImages={setUnassignedImages}
                      onCaptureHandlersReady={setCaptureHandlers}
                      poseLandmarker={poseLandmarkerVideo}
                      onShowTips={() => {
                        if (!hasShownTipsThisSession && appSettings.showPostureAnalysisTips !== false) {
                          setShowTipsDrawer(true);
                          setHasShownTipsThisSession(true);
                        }
                      }}
                      onReanalyze={handleReanalyze}
                      centerPanelMessage={centerPanelMessage}
                      classification={classification}
                      showClassificationOverlay={false}
                      onToggleClassificationOverlay={() => setAppSettings(prev => ({ ...prev, postureAnalysisPanelEnabled: !showAnalysisPanel }))}
                      rightReservationPx={rightReservationPx}
                      maxCanvasHeightPx={mobileSplitCanvasCapPx}
                      experimentalFeaturesEnabled={experimentalFeaturesEnabled}
                      fullscreenEditHistory={fullscreenEditHistoryForCapture}
                      onEditGestureStart={pushFullscreenEditHistory}
                      analysisData={analysisData}
                      model3D={model3D}
                      showAnalysisDashboardToggle={hasAnyAnalysisContent}
                      analysisPanelEnabled={fullscreenMetricsEnabled}
                      onToggleAnalysisPanel={() => setFullscreenMetricsEnabled((v) => !v)}
                      immersiveFullscreen={fillMobileViewport}
                      fillAvailable={fitPhotoToViewport}
                    />
                  </div>

                  {/* Analysis tiles column — right on md+, segmented split on narrow mobile, clean full-width column in self-assessment */}
                  {showAnalysisPanel && hasAnyAnalysisContent && (
                    <div className={cn(
                      mode.mode === 'self-assessment'
                        ? 'w-full min-w-0'
                        : useMobileSplit
                          ? mode.mode === 'self-assessment'
                            ? postureMobileSplitAnalysisColumnClass(useMobileSplit)
                            : 'max-h-[40%] min-h-0 w-full shrink-0 overflow-y-auto'
                          : fillDesktopViewport
                            ? 'h-full min-h-0 min-w-0 overflow-y-auto'
                            : 'min-w-[300px] sm:min-w-[340px] md:w-[380px] md:min-w-[380px] shrink-0 md:sticky md:top-[4.5rem] md:max-h-[calc(100vh-5.5rem)] md:overflow-y-auto',
                    )}>
                      <div
                        className={cn(
                          'w-full',
                          useMobileSplit && mode.mode === 'self-assessment' && 'flex min-h-0 flex-1 basis-0 flex-col',
                        )}
                      >
                      <AnalysisTiles
                        layout={analysisTilesLayout}
                        classification={classification}
                        analysisData={analysisData}
                        model3D={model3D}
                        capturedImages={capturedImages}
                        settings={settings}
                        currentView={currentView}
                        experimentalFeaturesEnabled={experimentalFeaturesEnabled}
                        defaultExpanded={mode.mode === 'self-assessment' ? false : true}
                        postureOrientationStrategyId={effectivePostureStrategyId}
                        onStrategyChange={
                          experimentalFeaturesEnabled
                            ? (id) => {
                                setSettings(prev => ({ ...prev, postureOrientationStrategyId: id }));
                                setAppSettings(prev => ({ ...prev, postureOrientationStrategyId: id }));
                              }
                            : undefined
                        }
                        practitionerObservations={practitionerObservations}
                        onObservationsChange={setPractitionerObservations}
                      />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="compare" className={cn(fillDesktopViewport && 'min-h-0 flex-1 overflow-y-auto')}>
              {/*
                S7/U6: both gate conditions now state their reason. Previously the trigger was
                rendered `disabled` with no explanation, so "no analysis yet" was indistinguishable
                from "broken". Ordering matters — the experimental gate is the outer one.
              */}
              {!experimentalFeaturesEnabled ? (
                <CompareUnavailableNotice reason="experimental-off" />
              ) : !isAnyAnalysisComplete ? (
                <CompareUnavailableNotice
                  reason="no-analysis"
                  onBackToCapture={() => setActiveTab('assessment')}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
                  <AnalysisSelector
                    onSelect={setComparisonAnalysis}
                    selectedId={comparisonAnalysis?.id}
                  />
                  {comparisonAnalysis ? (
                    <ComparisonView
                      analysisA={comparisonAnalysis} // History (Before)
                      analysisB={currentAnalysisForComparison} // Current (After)
                      experimentalFeaturesEnabled={experimentalFeaturesEnabled}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-muted/20 rounded-lg border border-dashed">
                      <p className="text-muted-foreground">Select a historical analysis to compare.</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

        </div>
      </div>

    </div>
  );
};

interface PostureAnalysisAppProps {
  onSelfAssessmentReportReady?: (analysis: PostureAnalysis) => void;
  onAnalysisUpdate?: (analysis: PostureAnalysis) => void;
  initialAnalysis?: PostureAnalysis | null;
}

const PostureAnalysisApp: React.FC<PostureAnalysisAppProps> = ({
  onSelfAssessmentReportReady,
  onAnalysisUpdate,
  initialAnalysis,
}) => {
  return (
    <PostureProvider>
      <PostureAnalysisContent
        onSelfAssessmentReportReady={onSelfAssessmentReportReady}
        onAnalysisUpdate={onAnalysisUpdate}
        initialAnalysis={initialAnalysis}
      />
    </PostureProvider>
  );
};

export default PostureAnalysisApp;
